import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CreateReferenceData, ErrorResponse, FilteredDropDownRequest, FilteredDropDownWithTypeModelInit, FilteredDropDownWithTypeResponse, ListModel, MainResponse, RejectedValue, TranslationList } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { TextBlockForDocuments } from "../../TextblockTemplates/state/textblock-template-interfaces";
import {
    Attachment, CreateDocumentNumberAndDateRequest, CreateDocumentNumberAndDateResponse,
    CreateFinanceDocumentRequest, CreateFinanceDocumentResponse, CreatePdfRequest,
    CreatePdfResponse, CreateReminderDunningRequest, DownloadDocumentResponse, DownloadFileResponse,
    FileUplaodResponse, FinanceDocumentApprovalResponse, FinanceDocumentDropdownLists, FinanceDocumentResponse,
    FinanceDocumentSliceModel, GetPositionCreateDataResponse, GetProjectDataResponse, InitPositionModel,
    InitReminderPositionModel, InvoiceBasicInfo, OfferBasicInfo, OrderBasicInfo, OrderConfirmationBasicInfo,
    PartnerDropdownListsResponse, PartnerTenantDropdownLists, PaymentDataRequest, Position, ReminderBasicInfo,
    ReminderPosition, RequestBasicInfo, SetPropValue, TenantLocationBankDataResponse,
    TextBlock, UpdateDocumentStatus, UpdatePositionDTO, UpdatePositionResponse, UpdatePositions,
    UpdateReminderPositionResponse, UpdateReminderPositionsRequest, UpdateTextBlocks, UpdateTextBlocksResponse,
    ExportFileDataResponse, initAttachmentsTabData, initPdfTabData, initPositionsTabData, initProjectData,
    initialBasicInfo, CostCenterData, CostCenterDetailsResponse, initialPaymentData, PaymentDataResponseWrapper, ExportFileDataRequest,
    initialExportFileDataResponse, ExportFileResponse, DocumentsAndFilesResponse, DocumentsAndFilesRequest,
    SetToApprovalReminderRequest, UpdateFileDescriptionRequest, AttachmentRequest,
    ProcedureInfo, ProcedureInfoFile, ProcedureFilesRequest, ProcedureFilesResponse, initialProcedureTabData, initialProcedureInfo,
    UpdateBasicInfoResponse, UpdateProcedureInfoResponse, UpdateProcedureInfoRequest, FileDeleteResponse, ContractListItem,
    GetContractListRequest, GetFileListForContractItemRequest, GetFileListForContractItemResponse, CheckDocumentNumberRequest,
    InvoceAndPaymentSumAmountResponse, ExportTXTFileRequest, ExportXMLFileRequest, GetFinanceRequestsResponse, GetOffersResponse,
    GetOrdersResponse, GetOrderConfirmationsResponse, GetInvoicesResponse, GetRemindersResponse, ReorderAttachmentsRequest,
    GetCreditNotesResponse, CreditNoteBasicInfo, DocumentListItem, DocumentListName, ListRowToRestore
} from "./finance-document-interfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { GetPdfDataRequest, PdfDataResponse, PdfSettingRequest, PdfSettingResponse } from "./finance-pdf-interfaces";
import { getUserTimeZone, downloadFile, GenerateGUID } from "shared/utils/common";
import { parseUnlessDate } from "shared/dayjs";
import { FinanceDocumentType, SourceTypes } from "constants/enums";
import { API_BASE_URL as baseUri } from "config/env";

//#region Requests
export const GetAllActiveRequests = createAsyncThunk<GetFinanceRequestsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveRequests',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/request-finance/get?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetFinanceRequestsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveRequests = createAsyncThunk<GetFinanceRequestsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveRequests',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/request-finance/get?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetFinanceRequestsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetRequestById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetRequestById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/request-finance/get-by-id?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendRequestToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendRequestToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateRequest = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateRequest',
    async (request: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/request-finance/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetRequestById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateRequestBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, RequestBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateRequestBasicInfo',
    async (requestBasicInfo: RequestBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(requestBasicInfo)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateRequestStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateRequestStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateRequest = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateRequest',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeRequests", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/request-finance/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The request is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateRequest = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateRequest',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveRequests", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/request-finance/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The request is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteRequest = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteRequest',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveRequests", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/request-finance/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The request is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateRequestPositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateRequestPositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/request-finance-position/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                requestId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateRequestTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateRequestTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/request-finance-text-block/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                requestId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadRequestFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/RequestFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance-file/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const RequestFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/RequestFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance-file/upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                requestId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteRequestFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteRequestFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/request-finance-file/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateRequestPdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateRequestPdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/request-finance/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);
//#endregion

//#region Offers

export const GetAllActiveOffers = createAsyncThunk<GetOffersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveOffers',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/offers/all?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOffersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveOffers = createAsyncThunk<GetOffersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveOffers',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/offers/all?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOffersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetOfferById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetOfferById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/offers/get?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendOfferToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendOfferToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateOffer = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOffer',
    async (offer: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/offers/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(offer)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetOfferById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateOfferBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, OfferBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOfferBasicInfo',
    async (offerTextblock: OfferBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(offerTextblock)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateOfferStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOfferStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateOffer = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateOffer',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeOffers", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/offers/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The offer is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateOffer = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateOffer',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOffers", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/offers/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The offer is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteOffer = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOffer',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOffers", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/offers/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The offer is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateOfferPositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOfferPositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/offers/update/position-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                offerId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateOfferTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOfferTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/offers/update/text-block-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                offerId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadOfferFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OfferFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/file-download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const OfferFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OfferFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/file-upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                offerId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteOfferFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOfferFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/offers/file-delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateOfferPdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOfferPdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/offers/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Orders

export const GetAllActiveOrders = createAsyncThunk<GetOrdersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveOrders',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/order/get?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOrdersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveOrders = createAsyncThunk<GetOrdersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveOrders',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/order/get?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOrdersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetOrderById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetOrderById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order/get-by-id?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendOrderToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendOrderToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateOrder = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOrder',
    async (order: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/order/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(order)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetOrderById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateOrderBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, OrderBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderBasicInfo',
    async (orderTextblock: OrderBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(orderTextblock)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateOrderStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateOrder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateOrder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeOrders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateOrder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateOrder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOrders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteOrder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOrder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOrders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateOrderPositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderPositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order-position/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateOrderTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order-text-block/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadOrderFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OrderFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-file/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const OrderFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OrderFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-file/upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteOrderFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOrderFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-file/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateOrderPdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOrderPdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Order confirmation

export const GetAllActiveOrderConfirmations = createAsyncThunk<GetOrderConfirmationsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveOrderConfirmations',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/order-confirmation/get?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOrderConfirmationsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveOrderConfirmations = createAsyncThunk<GetOrderConfirmationsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveOrderConfirmations',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/order-confirmation/get?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetOrderConfirmationsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetOrderConfirmationById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetOrderConfirmationById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order-confirmation/get-by-id?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendOrderConfirmationToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendOrderConfirmationToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateOrderConfirmation = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOrderConfirmation',
    async (order: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/order-confirmation/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(order)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetOrderConfirmationById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateOrderConfirmationBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, OrderConfirmationBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderConfirmationBasicInfo',
    async (orderTextblock: OrderConfirmationBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(orderTextblock)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateOrderConfirmationStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderConfirmationStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateOrderConfirmation = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateOrderConfirmation',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeOrderConfirmations", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order-confirmation/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order confirmation is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateOrderConfirmation = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateOrderConfirmation',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOrderConfirmations", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order-confirmation/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order confirmation is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteOrderConfirmation = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOrderConfirmation',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveOrderConfirmations", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/order-confirmation/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The order confirmation is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateOrderConfirmationPositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderConfirmationPositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order-confirmation-position/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderConfirmationId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateOrderConfirmationTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateOrderConfirmationTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order-confirmation-text-block/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderConfirmationId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadOrderConfirmationFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OrderConfirmationFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation-file/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const OrderConfirmationFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/OrderConfirmationFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation-file/upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                orderConfirmationId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteOrderConfirmationFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteOrderConfirmationFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation-file/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateOrderConfirmationPdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateOrderConfirmationPdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/order-confirmation/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Invoices

export const GetAllActiveInvoices = createAsyncThunk<GetInvoicesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveInvoices',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/invoices/all?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetInvoicesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveInvoices = createAsyncThunk<GetInvoicesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveInvoices',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/invoices/all?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetInvoicesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInvoiceById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetInvoiceById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/invoices/get?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInvoiceAndPaymentSum = createAsyncThunk<InvoceAndPaymentSumAmountResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetInvoiceAndPaymentSum',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/invoice-paid-amount?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as InvoceAndPaymentSumAmountResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const SendInvoiceToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendInvoiceToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateInvoice = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateInvoice',
    async (invoice: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/invoices/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(invoice)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetInvoiceById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateInvoiceBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, InvoiceBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateInvoiceBasicInfo',
    async (invoiceBasicInfo: InvoiceBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(invoiceBasicInfo)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateInvoiceStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateInvoiceStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateInvoice = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateInvoice',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeInvoices", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/invoices/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The invoice is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateInvoice = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateInvoice',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveInvoices", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/invoices/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The invoice is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteInvoice = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteInvoice',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveInvoices", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/invoices/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The invoice is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateInvoicePositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateInvoicePositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/invoices/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                invoiceId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateInvoiceTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateInvoiceTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/invoices/update/text-block-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                invoiceId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadInvoiceFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/InvoiceFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/file-download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const InvoiceFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/InvoiceFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/file-upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                invoiceId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteInvoiceFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteInvoiceFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/file-delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateInvoicePdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateInvoicePdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateUpdateInvoicePaymentData = createAsyncThunk<PaymentDataResponseWrapper, PaymentDataRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateUpdateInvoicePaymentData',
    async (data: PaymentDataRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/create-update-payment-data', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentDataResponseWrapper;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInvoicePaymentData = createAsyncThunk<PaymentDataResponseWrapper, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetInvoicePaymentData',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/invoices/get-payment-data?invoiceId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentDataResponseWrapper;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetExportFileData = createAsyncThunk<ExportFileDataResponse, ExportFileDataRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetExportFileData',
    async (data: ExportFileDataRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/invoices/get-data-for-export', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ExportFileDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendExportFileData = createAsyncThunk<ExportFileResponse, ExportTXTFileRequest | ExportXMLFileRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendExportFileData',
    async (data: ExportTXTFileRequest | ExportXMLFileRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/invoices/export-transfer-list', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ExportFileResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            // The field errors go along so the export grid can mark the rows the backend rejected.
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status, errors: errorResponse.errors });
        }
    }
);

//#endregion

//#region Credit Notes

export const GetAllActiveCreditNotes = createAsyncThunk<GetCreditNotesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveCreditNotes',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/credit-notes/all?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCreditNotesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveCreditNotes = createAsyncThunk<GetCreditNotesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveCreditNotes',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/credit-notes/all?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCreditNotesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCreditNoteById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetCreditNoteById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/credit-notes/get?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCreditNoteAndPaymentSum = createAsyncThunk<InvoceAndPaymentSumAmountResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetCreditNoteAndPaymentSum',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/credit-note-paid-amount?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as InvoceAndPaymentSumAmountResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const SendCreditNoteToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendCreditNoteToApproval',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/set-to-approve?id=' + id, {
            method: "PUT",
            headers: AuthHeader(true, true),
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateCreditNote = createAsyncThunk<CreateFinanceDocumentResponse, CreateFinanceDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateCreditNote',
    async (creditNote: CreateFinanceDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/credit-notes/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(creditNote)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetCreditNoteById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateCreditNoteBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, CreditNoteBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateCreditNoteBasicInfo',
    async (creditNoteBasicInfo: CreditNoteBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(creditNoteBasicInfo)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateCreditNoteStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateCreditNoteStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateCreditNote = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateCreditNote',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeCreditNotes", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/credit-notes/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The credit note is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateCreditNote = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateCreditNote',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveCreditNotes", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/credit-notes/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The credit note is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteCreditNote = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteCreditNote',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveCreditNotes", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/credit-notes/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The credit note is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateCreditNotePositions = createAsyncThunk<UpdatePositionResponse, UpdatePositions, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateCreditNotePositions',
    async (positions: UpdatePositions, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/credit-notes/update-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                creditNoteId: positions.entityId,
                positions: positions.positions,
                editedPositionId: positions.editedPositionId,
                action: positions.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdatePositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateCreditNoteTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateCreditNoteTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/credit-notes/update/text-block-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                creditNoteId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadCreditNoteFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreditNoteFileDownload',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/file-download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreditNoteFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreditNoteFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/file-upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                creditNoteId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteCreditNoteFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteCreditNoteFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/file-delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateCreditNotePdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateCreditNotePdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/credit-notes/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Reminder and Dunning
export const GetAllActiveReminders = createAsyncThunk<GetRemindersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllActiveReminders',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/reminders/all?active=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetRemindersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveReminders = createAsyncThunk<GetRemindersResponse, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllInactiveReminders',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/reminders/all?active=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetRemindersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetReminderById = createAsyncThunk<FinanceDocumentResponse, { id: string, withFiles?: boolean }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetReminderById',
    async (params: { id: string, withFiles?: boolean }, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/reminders/get?id=' + params.id + '&files=' + (params.withFiles ? params.withFiles : false), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const SendReminderToApproval = createAsyncThunk<FinanceDocumentApprovalResponse, SetToApprovalReminderRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/SendReminderToApproval',
    async (model: SetToApprovalReminderRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/set-to-approve', {
            method: "PUT",
            headers: AuthHeader(true, true),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const approvalResponse = await response.json() as FinanceDocumentApprovalResponse;
            return approvalResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateReminder = createAsyncThunk<CreateFinanceDocumentResponse, CreateReminderDunningRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateReminder',
    async (reminder: CreateReminderDunningRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/reminders/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(reminder)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreateFinanceDocumentResponse;
            dispatch(GetReminderById({ id: createResponse.id.toString() }))
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateReminderBasicInfo = createAsyncThunk<UpdateBasicInfoResponse, ReminderBasicInfo, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateReminderBasicInfo',
    async (basicInfo: ReminderBasicInfo, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(basicInfo)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as UpdateBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateReminderStatus = createAsyncThunk<string, UpdateDocumentStatus, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateReminderStatus',
    async (documentStatus: UpdateDocumentStatus, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(documentStatus)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateReminder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeactivateReminder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("activeReminders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/reminders/deactivate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The reminder is successfully deactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const ReactivateReminder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReactivateReminder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveReminders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/reminders/activate?id=' + id, {
                method: "PUT",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The reminder is successfully reactivated.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const DeleteReminder = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteReminder',
    async (id: string, { rejectWithValue, dispatch, getState }) => {
        const putRowBack = takeRowOut("inactiveReminders", id, dispatch, getState);
        try {
            const response = await fetch(baseUri + 'api/reminders/delete?id=' + id, {
                method: "DELETE",
                headers: AuthHeader(),
            });
            if (response.status === 200) {
                const responseModel = await response.json() as MainResponse;
                dispatch(showNotification({ message: "The reminder is successfully deleted.", type: NotificationTypes.Success }));
                return responseModel;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                putRowBack();
                dispatch(showNotification({ message: errorResponse.detail ?? "Error in application", type: NotificationTypes.Error }));
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error) {
            // A request that never came back - the network dropped, the server did not answer -
            // would otherwise leave the row out of a list that still holds it.
            putRowBack();
            dispatch(showNotification({ message: "Error in application", type: NotificationTypes.Error }));
            throw error;
        }
    }
);

export const UpdateReminderPositions = createAsyncThunk<UpdateReminderPositionResponse, UpdateReminderPositionsRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateReminderPositions',
    async (positions: UpdateReminderPositionsRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/reminders/update/position-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(positions)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateReminderPositionResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateReminderTextblocks = createAsyncThunk<UpdateTextBlocksResponse, UpdateTextBlocks, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateReminderTextblocks',
    async (textblocks: UpdateTextBlocks, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/reminders/update/text-block-batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({
                reminderId: textblocks.entityId,
                textblocks: textblocks.textBlocks,
                editedTextblockId: textblocks.editedTextBlockId,
                action: textblocks.action
            })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateTextBlocksResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadReminderFile = createAsyncThunk<DownloadFileResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DownloadReminderFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/file-download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadFileResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const ReminderFileUpload = createAsyncThunk<FileUplaodResponse, AttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReminderFileUpload',
    async (request: AttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/file-upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({
                reminderId: request.id,
                files: request.files
            })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileUplaodResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteReminderFile = createAsyncThunk<FileDeleteResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteReminderFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/reminders/file-delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as FileDeleteResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateReminderPdfDocument = createAsyncThunk<CreatePdfResponse, CreatePdfRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateReminderPdfDocument',
    async (data: CreatePdfRequest, { rejectWithValue }) => {
        //TODO CHANGE URL
        const response = await fetch(baseUri + 'api/reminders/upload-document', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePdfResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Contract list
export const GetAllContractListItems = createAsyncThunk<ContractListItem[], GetContractListRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllContractListItems',
    async (request: GetContractListRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/contracts-list', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ContractListItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetFilesForContractListItem = createAsyncThunk<GetFileListForContractItemResponse, GetFileListForContractItemRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetFilesForContractListItem',
    async (request: GetFileListForContractItemRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/order/get?active=true', {
            method: "GET",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetFileListForContractItemResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);
//#endregion

//#region Shared

export const GetPdfData = createAsyncThunk<PdfDataResponse, GetPdfDataRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetPdfData',
    async (data: GetPdfDataRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/pdf-data', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PdfDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCreateData = createAsyncThunk<FinanceDocumentDropdownLists, void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetCreateData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FinanceDocumentDropdownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetFilteredPartnersAndTenantsForDropdown = createAsyncThunk<FilteredDropDownWithTypeResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetFilteredPartnersAndTenantsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance/partner-tenant-dropdown-list', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownWithTypeResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProjectData = createAsyncThunk<GetProjectDataResponse, { id: string, tenantId: string }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetProjectData',
    async ({ id, tenantId }, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance/project-data?id=' + id + '&tenant=' + tenantId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetProjectDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTenantDropdownLists = createAsyncThunk<PartnerTenantDropdownLists, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetTenantDropdownLists',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/tenant-dropdown-lits?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PartnerTenantDropdownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTenantLocationBankDataDropdownList = createAsyncThunk<TenantLocationBankDataResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetTenantLocationBankDataDropdownList',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/tenant-location-bank-list?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TenantLocationBankDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnerDropdownListsForInternal = createAsyncThunk<PartnerTenantDropdownLists, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetPartnerDropdownListsForInternal',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/tenant-dropdown-lits?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PartnerTenantDropdownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnerLocationBankDataDropdownListForInternal = createAsyncThunk<TenantLocationBankDataResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetPartnerLocationBankDataDropdownListForInternal',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/tenant-location-bank-list?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TenantLocationBankDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnerDropdownListsForExternal = createAsyncThunk<PartnerDropdownListsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetPartnerDropdownListsForExternal',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/partner-dropdown-lits?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PartnerDropdownListsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPositionCreateData = createAsyncThunk<GetPositionCreateDataResponse, number, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetPositionCreateData',
    async (procedureTypeId: number, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/get-position-create-data?procedureTypeId=' + procedureTypeId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPositionCreateDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateDocumentNumberAndDate = createAsyncThunk<CreateDocumentNumberAndDateResponse, CreateDocumentNumberAndDateRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateDocumentNumberAndDate',
    async (data: CreateDocumentNumberAndDateRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/document-assign-number', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreateDocumentNumberAndDateResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetIncomingDocumentNumber = createAsyncThunk<{ documentNumber: string }, { type: FinanceDocumentType, tenantId: string }, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetIncomingDocumentNumber',
    async ({ type, tenantId }, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance/generate/incoming-number?type=' + type + '&tenant=' + tenantId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { documentNumber: string };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadDocumentFile = createAsyncThunk<DownloadDocumentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DownloadDocumentFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance/download-document?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DownloadDocumentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetDocumentsAndFiles = createAsyncThunk<DocumentsAndFilesResponse, DocumentsAndFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetDocumentsAndFiles',
    async (data: DocumentsAndFilesRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance/documents-and-files?documentId=' + data.documentId + '&documentTypeId=' + data.documentTypeId, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DocumentsAndFilesResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetAllCostCenters = createAsyncThunk<CostCenterData[], void, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetAllCostCenters',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance-overview/costcenters', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CostCenterData[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCostCenterDetails = createAsyncThunk<CostCenterDetailsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetCostCenterDetails',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/finance-overview/documents?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CostCenterDetailsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateFileDescription = createAsyncThunk<string, UpdateFileDescriptionRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateFileDescription',
    async (data: UpdateFileDescriptionRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/update-file-description', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as string;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReorderAttachments = createAsyncThunk<string, ReorderAttachmentsRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/ReorderAttachments',
    async (data: ReorderAttachmentsRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/attachments/reorder', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as string;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateProcedure = createAsyncThunk<UpdateProcedureInfoResponse, UpdateProcedureInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateProcedure',
    async (data: UpdateProcedureInfoRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateProcedureInfoResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UploadProcedureFile = createAsyncThunk<ProcedureFilesResponse, ProcedureFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UploadProcedureFile',
    async (data: ProcedureFilesRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/files/upload', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProcedureFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteProcedureFile = createAsyncThunk<ProcedureFilesResponse, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DeleteProcedureFile',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/files/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProcedureFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateProcedureFile = createAsyncThunk<ProcedureFilesResponse, ProcedureInfoFile, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateProcedureFile',
    async (data: ProcedureInfoFile, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/files/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProcedureFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadProcedureFile = createAsyncThunk<ProcedureInfoFile, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/DownloadProcedureFile',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/files/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProcedureInfoFile;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProcedure = createAsyncThunk<ProcedureInfo, string, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/GetProcedure',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/get?procedureId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProcedureInfo;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CheckFinanceDocumentNumberAvailability = createAsyncThunk<boolean, CheckDocumentNumberRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CheckFinanceDocumentNumberAvailability',
    async (data: CheckDocumentNumberRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/check-document-number', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as boolean;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateDocumentPdfSettings = createAsyncThunk<PdfSettingResponse, PdfSettingRequest, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/UpdateDocumentPdfSettings',
    async (data: PdfSettingRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/pdf-settings', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PdfSettingResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Create reference data

export const CreateFinanceSource = createAsyncThunk<TranslationList[], CreateReferenceData, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateFinanceSource',
    async (data: CreateReferenceData, { rejectWithValue, getState }) => {
        const response = await fetch(baseUri + 'api/source/create-from-dropdown', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ name: data.name, type: SourceTypes.Finance })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as TranslationList[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateCategory = createAsyncThunk<TranslationList[], CreateReferenceData, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreateCategory',
    async (data: CreateReferenceData, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/create-from-dropdown', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as TranslationList[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreatePaymentMethod = createAsyncThunk<TranslationList[], CreateReferenceData, { rejectValue: RejectedValue }>(
    'Slice/FinanceDocumentSlice/CreatePaymentMethod',
    async (data: CreateReferenceData, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/create-from-dropdown', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as TranslationList[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

const initialValue: FinanceDocumentSliceModel = {
    statusCode: "",
    isLoading: false,
    isModalLoading: false,
    message: "",
    showNotification: false,
    isUploadSpaceOpen: false,
    dropdownLists: null,
    filteredPartnersAndTenantsList: FilteredDropDownWithTypeModelInit,
    tenantDropdownLists: { locations: [], employees: [], bankDatas: [] },
    partnerDropdownLists: { locations: [], employees: [], bankDatas: [] },
    tenantPdfData: null,
    recipientPdfData: null,
    financeDocumentId: "",
    financeDocumentTypeId: "",
    financeDocumentRequestStatusId: null,
    financeDocumentCreateModelValid: false,
    basicInfoTabData: initialBasicInfo,
    procedureInfoTabData: initialProcedureTabData,
    textblockTabData: { textblockForEdit: null, selectedTextblocks: [], lastValidData: [] },
    attachmentsTabData: initAttachmentsTabData,
    projectData: initProjectData,
    positionsTabData: initPositionsTabData,
    pdfTabData: initPdfTabData,
    documentLists: {
        activeRequests: [],
        inactiveRequests: [],
        activeOffers: [],
        inactiveOffers: [],
        activeOrders: [],
        inactiveOrders: [],
        activeOrderConfirmations: [],
        inactiveOrderConfirmations: [],
        activeInvoices: [],
        inactiveInvoices: [],
        activeReminders: [],
        inactiveReminders: [],
        contractList: [],
        overviewsByCostCenter: [],
        costCenterDetails: [],
        activeCreditNotes: [],
        inactiveCreditNotes: []
    },
    relatedFinanceDocumentNumbers: {},
    prevFinanceDocument: { id: "", propName: "" },
    paymentsTabData: initialPaymentData,
    exportFileDataTab: initialExportFileDataResponse,
    financeDocumentDatabaseState: {
        basicInfoTabData: initialBasicInfo,
        procedureInfo: initialProcedureInfo
    },
    sourceInfo: {
        isSourceDropdownLoading: false,
        newSourceId: null
    },
    invoiceAndPaymentDetails: { currency: '', invoiceAmount: 0, paidAmount: 0, invoiceId: '', invoiceDate: null, invoiceNumber: null, tenantAbbreviation: "" },
    /* reminderPositionPayload: { invoiceDate: null, invoiceNumber: null }, */
    categoryInfo: {
        isCategoryDropdownLoading: false,
        newCategoryId: null
    },
    paymentMethodInfo: {
        isPaymentMethodDropdownLoading: false,
        newPaymentMethodId: null
    },
    permissions: {
        createEditTenants: [],
        reactivateDeactivateTenants: [],
        deleteTenants: []
    }
}

// parseInt returns NaN for null/undefined/"" and `?? null` does not catch NaN, so the store used to
// end up holding NaN. That leaks into number inputs (React "specified value NaN" warnings) and makes
// react-hook-form report the form as dirty forever, because its deep compare uses !== and NaN !== NaN.
const toIntOrNull = (value: any): number | null => {
    const parsed = parseInt(value);
    return Number.isNaN(parsed) ? null : parsed;
};

// Deactivating, reactivating or deleting a document takes its row out of the list straight away, so
// that the table answers the click rather than the list endpoint, which is slow because of
// everything it carries. The row is held in the thunk until the request comes back - never in the
// store, which is persisted, so a reload can never resurrect it - and put back on a failure.
function takeRowOut(list: DocumentListName, id: string, dispatch: (action: any) => void, getState: () => any) {
    const items = (getState().financeDocumentSlice.documentLists[list] ?? []) as DocumentListItem[];
    const index = items.findIndex(item => item.id === id);
    if (index === -1) {
        return () => { };
    }
    const item = items[index];
    dispatch(removeDocumentFromList({ list, id }));
    return () => dispatch(restoreDocumentToList({ list, index, item }));
}

export const FinanceDocumentSlice = createSlice({
    name: 'FinanceDocument',
    initialState: initialValue,
    reducers: {
        removeDocumentFromList: (state, action: PayloadAction<{ list: DocumentListName, id: string }>) => {
            const items = state.documentLists[action.payload.list] as DocumentListItem[];
            const index = items.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
                items.splice(index, 1);
            }
        },
        restoreDocumentToList: (state, action: PayloadAction<ListRowToRestore>) => {
            const items = state.documentLists[action.payload.list] as DocumentListItem[];
            // The list can have been reloaded while the request was out, so only put the row back when
            // it really is gone, and never past the end of a list that has since become shorter.
            if (!items.some(item => item.id === action.payload.item.id)) {
                items.splice(Math.min(action.payload.index, items.length), 0, action.payload.item);
            }
        },
        resetFinanceDocumentSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.isUploadSpaceOpen = false;
            state.showNotification = false;
            state.message = '';
            state.dropdownLists = null;
            state.filteredPartnersAndTenantsList = FilteredDropDownWithTypeModelInit;
            state.tenantDropdownLists = { locations: [], employees: [], bankDatas: [] };
            state.partnerDropdownLists = { locations: [], employees: [], bankDatas: [] };
            state.financeDocumentId = "";
            state.financeDocumentTypeId = "";
            state.financeDocumentCreateModelValid = false;
            state.basicInfoTabData = initialBasicInfo;
            state.procedureInfoTabData = initialProcedureTabData;
            state.textblockTabData = { textblockForEdit: null, selectedTextblocks: [], lastValidData: [] };
            state.attachmentsTabData = initAttachmentsTabData;
            state.projectData = initProjectData;
            state.positionsTabData = initPositionsTabData;
            state.pdfTabData = initPdfTabData;
            state.relatedFinanceDocumentNumbers = {};
            state.financeDocumentRequestStatusId = null;
            state.paymentsTabData = initialPaymentData;
            state.exportFileDataTab = initialExportFileDataResponse;
            state.tenantPdfData = null;
            state.recipientPdfData = null;
            state.financeDocumentDatabaseState = {
                basicInfoTabData: initialBasicInfo,
                procedureInfo: initialProcedureInfo
            };
            state.sourceInfo = {
                isSourceDropdownLoading: false,
                newSourceId: null
            };
            state.categoryInfo = {
                isCategoryDropdownLoading: false,
                newCategoryId: null
            };
            state.paymentMethodInfo = {
                isPaymentMethodDropdownLoading: false,
                newPaymentMethodId: null
            };
            state.invoiceAndPaymentDetails = { currency: '', invoiceAmount: 0, paidAmount: 0, invoiceId: '', invoiceDate: null, invoiceNumber: null, tenantAbbreviation: "" };
        },
        resetSource: (state) => {
            state.sourceInfo = {
                isSourceDropdownLoading: false,
                newSourceId: null
            }
        },
        resetDocument: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.isUploadSpaceOpen = false;
            state.showNotification = false;
            state.message = '';
            state.filteredPartnersAndTenantsList = FilteredDropDownWithTypeModelInit;
            state.tenantDropdownLists = { locations: [], employees: [], bankDatas: [] };
            state.partnerDropdownLists = { locations: [], employees: [], bankDatas: [] };
            state.financeDocumentId = "";
            state.financeDocumentTypeId = "";
            state.financeDocumentCreateModelValid = false;
            state.basicInfoTabData = initialBasicInfo;
            state.procedureInfoTabData = initialProcedureTabData;
            state.textblockTabData = { textblockForEdit: null, selectedTextblocks: [], lastValidData: [] };
            state.attachmentsTabData = initAttachmentsTabData
            state.projectData = initProjectData;
            state.positionsTabData = initPositionsTabData;
            state.pdfTabData = initPdfTabData;
            state.tenantPdfData = null;
            state.recipientPdfData = null;
            state.relatedFinanceDocumentNumbers = {}
            state.financeDocumentRequestStatusId = null;
            state.paymentsTabData = initialPaymentData;
            state.exportFileDataTab = initialExportFileDataResponse;
            state.financeDocumentDatabaseState = {
                basicInfoTabData: initialBasicInfo,
                procedureInfo: initialProcedureInfo
            };
            state.sourceInfo = {
                isSourceDropdownLoading: false,
                newSourceId: null
            };
            state.categoryInfo = {
                isCategoryDropdownLoading: false,
                newCategoryId: null
            };
            state.paymentMethodInfo = {
                isPaymentMethodDropdownLoading: false,
                newPaymentMethodId: null
            };
            state.invoiceAndPaymentDetails = { currency: '', invoiceAmount: 0, paidAmount: 0, invoiceId: '', invoiceDate: null, invoiceNumber: null, tenantAbbreviation: "" };
        },
        resetDocumentId: (state) => {
            state.financeDocumentId = "";
        },
        resetTenantLocationsBankData: (state) => {
            state.tenantDropdownLists.bankDatas = [];
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        setIsUploadSpaceOpen: (state, action: PayloadAction<boolean>) => {
            state.isUploadSpaceOpen = action.payload;
        },
        setBasicInfoTabProp: (state, action: PayloadAction<SetPropValue>) => {
            switch (action.payload.propName) {
                case 'id':
                    state.basicInfoTabData.id = action.payload.value;
                    break;
                case 'isOutgoing':
                    state.basicInfoTabData.isOutgoing = action.payload.value;
                    break;
                case 'typeId':
                    state.basicInfoTabData.typeId = action.payload.value;
                    break;
                case 'statusId':
                    state.basicInfoTabData.statusId = action.payload.value;
                    break;
                case 'documentDate':
                    state.basicInfoTabData.documentDate = action.payload.value;
                    break;
                case 'documentNumber':
                    state.basicInfoTabData.documentNumber = action.payload.value;
                    break;
                case 'currency':
                    state.basicInfoTabData.currency = action.payload.value;
                    break;
                case 'subject':
                    state.basicInfoTabData.subject = action.payload.value;
                    break;
                case 'note':
                    state.basicInfoTabData.note = action.payload.value;
                    break;
                case 'comment':
                    state.basicInfoTabData.comment = action.payload.value;
                    break;
                case 'bindingDeadline':
                    (state.basicInfoTabData as OfferBasicInfo | OrderBasicInfo).bindingDeadline = action.payload.value;
                    break;
                case 'reverseChargeTax':
                    state.basicInfoTabData.reverseChargeTax = action.payload.value;
                    break;
                case 'vat':
                    state.basicInfoTabData.vat = action.payload.value;
                    break;

                // Request specific props
                case 'sourceId':
                    (state.basicInfoTabData as RequestBasicInfo).sourceId = action.payload.value;
                    break;

                // Offer specific props
                case 'requestId':
                    (state.basicInfoTabData as OfferBasicInfo).requestId = action.payload.value;
                    break;

                // Order specific props
                case 'offerId':
                    (state.basicInfoTabData as OrderBasicInfo).offerId = action.payload.value;
                    break;

                // Order confirmation specific props
                case 'orderId':
                    (state.basicInfoTabData as OrderConfirmationBasicInfo).orderId = action.payload.value;
                    break;

                // Invoice specific props
                case 'paymentDays':
                    (state.basicInfoTabData as InvoiceBasicInfo).paymentDays = action.payload.value;
                    break;
                case 'discountDays':
                    (state.basicInfoTabData as InvoiceBasicInfo).discountDays = action.payload.value;
                    break;
                case 'discount':
                    (state.basicInfoTabData as InvoiceBasicInfo).discount = action.payload.value;
                    break;
                case 'deliveryPeriodFrom':
                    (state.basicInfoTabData as InvoiceBasicInfo).deliveryPeriodFrom = action.payload.value;
                    break;
                case 'deliveryPeriodTo':
                    (state.basicInfoTabData as InvoiceBasicInfo).deliveryPeriodTo = action.payload.value;
                    break;
                case 'periodFrom':
                    (state.basicInfoTabData as InvoiceBasicInfo).periodFrom = action.payload.value;
                    break;
                case 'periodTo':
                    (state.basicInfoTabData as InvoiceBasicInfo).periodTo = action.payload.value;
                    break;
                case 'orderConfirmationId':
                    (state.basicInfoTabData as InvoiceBasicInfo).orderConfirmationId = action.payload.value;
                    break;
                case 'paymentMethodId':
                    (state.basicInfoTabData as InvoiceBasicInfo).paymentMethodId = action.payload.value;
                    break;
                case 'paidByCash':
                    (state.basicInfoTabData as InvoiceBasicInfo).paidByCash = action.payload.value;
                    break;

                // Credit Note specific props
                case 'isFullCancellation':
                    (state.basicInfoTabData as CreditNoteBasicInfo).isFullCancellation = action.payload.value;
                    break;

                // Reminder specific props
                case 'documentTypeId':
                    (state.basicInfoTabData as ReminderBasicInfo).documentTypeId = action.payload.value;
                    break;
                case 'invoiceId':
                    (state.basicInfoTabData as ReminderBasicInfo).invoiceId = action.payload.value;
                    break;

                default:
                    console.log("Missing prop name: " + action.payload.propName)
                    break;
            }
        },
        setProcedureInfoTabProp: (state, action: PayloadAction<SetPropValue>) => {
            switch (action.payload.propName) {
                case 'procedureId':
                    state.procedureInfoTabData.procedureInfo.procedureId = action.payload.value;
                    break;
                case 'docType':
                    state.procedureInfoTabData.procedureInfo.docType = toIntOrNull(action.payload.value) ?? state.procedureInfoTabData.procedureInfo.docType;
                    break;
                case 'name':
                    state.procedureInfoTabData.procedureInfo.name = action.payload.value;
                    break;
                case 'projectId':
                    state.procedureInfoTabData.procedureInfo.projectId = action.payload.value;
                    break;
                case 'partOfExistingProcedure':
                    state.procedureInfoTabData.procedureInfo.partOfExistingProcedure = action.payload.value;
                    break;
                case 'noCostCenter':
                    state.procedureInfoTabData.procedureInfo.noCostCenter = action.payload.value;
                    break;
                case 'projectName':
                    state.procedureInfoTabData.procedureInfo.projectName = action.payload.value;
                    break;
                case 'procedureTypeId':
                    state.procedureInfoTabData.procedureInfo.procedureTypeId = toIntOrNull(action.payload.value);
                    break;
                case 'contractStartDate':
                    state.procedureInfoTabData.procedureInfo.contractStartDate = action.payload.value;
                    break;
                case 'contractEndDate':
                    state.procedureInfoTabData.procedureInfo.contractEndDate = action.payload.value;
                    break;
                case 'contractTypeId':
                    state.procedureInfoTabData.procedureInfo.contractTypeId = toIntOrNull(action.payload.value);
                    break;
                case 'contractNumber':
                    state.procedureInfoTabData.procedureInfo.contractNumber = action.payload.value;
                    break;
                case 'paymentIntervalId':
                    state.procedureInfoTabData.procedureInfo.paymentIntervalId = toIntOrNull(action.payload.value);
                    break;
                case 'invoiceEmail':
                    state.procedureInfoTabData.procedureInfo.invoiceEmail = action.payload.value;
                    break;
                case 'ourCostcenterEmail':
                    state.procedureInfoTabData.procedureInfo.ourCostcenterEmail = action.payload.value;
                    break;
                case 'note':
                    state.procedureInfoTabData.procedureInfo.note = action.payload.value;
                    break;
                case 'noticePeriod':
                    state.procedureInfoTabData.procedureInfo.noticePeriod = toIntOrNull(action.payload.value);
                    break;
                case 'partnerProjectName':
                    state.procedureInfoTabData.procedureInfo.partnerProjectName = action.payload.value;
                    break;
                case 'plannedHours':
                    state.procedureInfoTabData.procedureInfo.plannedHours = toIntOrNull(action.payload.value);
                    break;
                case 'amountPerHour':
                    state.procedureInfoTabData.procedureInfo.amountPerHour = toIntOrNull(action.payload.value);
                    break;
                case 'totalAmount':
                    state.procedureInfoTabData.procedureInfo.totalAmount = toIntOrNull(action.payload.value);
                    break;
                case 'currancy':
                    state.procedureInfoTabData.procedureInfo.currancy = action.payload.value;
                    break;
                case 'files':
                    state.procedureInfoTabData.procedureInfo.files = action.payload.value;
                    break;
                case 'extensionId':
                    state.procedureInfoTabData.procedureInfo.extensionId = action.payload.value;
                    break;
                case 'categoryId':
                    state.procedureInfoTabData.procedureInfo.categoryId = action.payload.value;
                    break;
                case 'paymentMethodId':
                    state.procedureInfoTabData.procedureInfo.paymentMethodId = action.payload.value;
                    break;
                case 'relation':
                    state.procedureInfoTabData.procedureInfo.relation = action.payload.value;
                    break;
                case 'clientNumber':
                    state.procedureInfoTabData.procedureInfo.clientNumber = action.payload.value;
                    break;

                case 'tenantId':
                    state.procedureInfoTabData.procedureInfo.tenantId = action.payload.value;
                    break;
                case 'tenantLocationId':
                    state.procedureInfoTabData.procedureInfo.tenantLocationId = action.payload.value;
                    break;
                case 'tenantResponsiblePersonId':
                    state.procedureInfoTabData.procedureInfo.tenantResponsiblePersonId = action.payload.value;
                    break;
                case 'tenantLocationBankDataId':
                    state.procedureInfoTabData.procedureInfo.tenantLocationBankDataId = action.payload.value;
                    break;

                case 'partnerId':
                    state.procedureInfoTabData.procedureInfo.partnerId = action.payload.value;
                    break;
                case 'contactId':
                    state.procedureInfoTabData.procedureInfo.contactId = action.payload.value;
                    break;
                case 'partnerLocationId':
                    state.procedureInfoTabData.procedureInfo.partnerLocationId = action.payload.value;
                    break;
                case 'partnerBankDataId':
                    state.procedureInfoTabData.procedureInfo.partnerBankDataId = action.payload.value;
                    break;
                case 'depositPercent':
                    state.procedureInfoTabData.procedureInfo.depositPercent = action.payload.value;
                    break;
                case 'securityDeposit':
                    state.procedureInfoTabData.procedureInfo.securityDeposit = action.payload.value;
                    break;
                default:
                    console.log("Missing prop name: " + action.payload.propName)
                    break;
            }
        },
        resetProcedureInfoTabProps: (state) => {
            state.procedureInfoTabData.procedureInfo = {
                ...initialProcedureInfo,
                docType: state.procedureInfoTabData.procedureInfo.docType,
                procedureTypeId: state.procedureInfoTabData.procedureInfo.procedureTypeId,
                tenantId: state.procedureInfoTabData.procedureInfo.tenantId,
                noCostCenter: state.procedureInfoTabData.procedureInfo.noCostCenter
            };
        },
        resetProcedureInfoTabParticipantsAndContractData: (state) => {
            state.procedureInfoTabData.procedureInfo = {
                ...initialProcedureInfo,
                docType: state.procedureInfoTabData.procedureInfo.docType,
                procedureTypeId: state.procedureInfoTabData.procedureInfo.procedureTypeId,
                tenantId: state.procedureInfoTabData.procedureInfo.tenantId,
                projectId: state.procedureInfoTabData.procedureInfo.projectId,
                noCostCenter: state.procedureInfoTabData.procedureInfo.noCostCenter
            };
        },
        setPrevFinanceDocument: (state, action: PayloadAction<SetPropValue>) => {
            state.prevFinanceDocument.id = action.payload.value
            state.prevFinanceDocument.propName = action.payload.propName
        },
        resetPrevFinanceDocument: (state) => {
            state.prevFinanceDocument.id = ""
            state.prevFinanceDocument.propName = ""
        },
        setSelectedTextblocksInTab: (state, action: PayloadAction<TextBlockForDocuments[]>) => {
            const requests: TextBlock[] = action.payload.map(response => ({
                id: response.id,
                name: response.name,
                positionId: response.positionId,
                orderInPosition: response.orderInPosition,
                defaultText: response.defaultText,
                defaultLanguage: response.defaultLanguage,
                textTranslations: response.textTranslations,
                textBlockId: response.textBlockId,
            }));
            var sortedTextblocks = requests.sort((a, b) => {
                if (a.positionId !== b.positionId) {
                    return a.positionId - b.positionId;
                }
                return a.orderInPosition - b.orderInPosition;
            });
            state.textblockTabData.selectedTextblocks = sortedTextblocks;
        },
        setTextblockFromTemplate: (state, action: PayloadAction<TextBlock>) => {
            state.textblockTabData.textblockForEdit = action.payload;
        },
        setAttachmentFiles: (state, action: PayloadAction<Attachment[]>) => {
            state.attachmentsTabData.attachments = action.payload;
        },
        // addAttachment: (state, action: PayloadAction<Attachment>) => {
        //     let attachments = [...state.attachmentsTabData.attachments];

        //     if (action.payload.isMainDocument) {
        //         attachments.forEach(attachment => {
        //             if (attachment.isMainDocument) {
        //                 attachment.isMainDocument = false;
        //             }
        //         });
        //         //TESTIRAJ KAKO RADI PRI KREIRANJU NOVOG DOKUMENTA
        //     }

        //     attachments.push(action.payload);
        //     attachments.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));

        //     state.attachmentsTabData.attachments = attachments;
        // },
        // updateAttachment: (state, action: PayloadAction<Attachment>) => {
        //     const attachmentsToUpdate = [...state.attachmentsTabData.attachments]
        //     const idForUpdate = attachmentsToUpdate.findIndex(obj => obj.id === action.payload.id);

        //     if (idForUpdate !== -1) {
        //         if (action.payload.isMainDocument) {
        //             attachmentsToUpdate.forEach(attachment => {
        //                 if (attachment.isMainDocument) {
        //                     attachment.isMainDocument = false;
        //                 }
        //             });
        //         }
        //         attachmentsToUpdate[idForUpdate] = action.payload;
        //         //TESTIRAJ KAKO RADI PRI KREIRANJU NOVOG DOKUMENTA
        //     }
        //     attachmentsToUpdate.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));

        //     state.attachmentsTabData.attachments = attachmentsToUpdate;
        // },
        deleteAttachmentInSlice: (state, action: PayloadAction<string>) => {
            const attachmentsToUpdate = [...state.attachmentsTabData.attachments]
            const idForDelete = attachmentsToUpdate.findIndex(obj => obj.id === action.payload);
            attachmentsToUpdate.splice(idForDelete, 1);
            attachmentsToUpdate.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            state.attachmentsTabData.attachments = attachmentsToUpdate;
        },
        // downloadLocalAttachment: (state, action: PayloadAction<string>) => {
        //     const attachment = state.attachmentsTabData.attachments.find(a => a.id === action.payload);
        //     if (attachment?.fileBase64) {
        //         downloadFile(attachment?.fileName, attachment?.fileBase64);
        //     }
        // },
        resetProjectData: (state) => {
            state.projectData.projectTemplates = [];
            state.projectData.projectBasicInfo = null;
            state.projectData.existingProcedures = [];
        },
        resetUpdatePositionsInDb: (state) => {
            state.positionsTabData.updatePositionsInDb = false;
        },
        resetPosition: (state) => {
            state.positionsTabData.position = { ...InitPositionModel };
        },
        setPosition: (state, action: PayloadAction<Position>) => {
            state.positionsTabData.position = action.payload;
        },
        addArticleInPosition: (state, action: PayloadAction<string>) => {
            state.positionsTabData.position.articleId = action.payload;
        },
        setPositions: (state, action: PayloadAction<Position[]>) => {
            state.positionsTabData.updatePositionsInDb = true;
            state.positionsTabData.positions = action.payload;
        },
        /*   generateNewIdsForDocumentPositions: (state) => {
              var newData = state.positionsTabData.positions;
              newData.forEach((item: Position, index: number) => {
                  item.id = GenerateGUID();
  
              });
              state.positionsTabData.positions = newData;
          }, */
        generateNewIdsForDocumentTextBlocks: (state) => {
            var newData = state.textblockTabData.selectedTextblocks;
            newData.forEach((item: TextBlock, index: number) => {
                item.id = GenerateGUID();

            });
            state.textblockTabData.selectedTextblocks = newData;
        },
        setNewPositionIds: (state) => {
            var newData = state.positionsTabData.positions;
            newData.forEach((item: Position, index: number) => {
                item.id = GenerateGUID();

                if (item.subRows != null && item.subRows !== undefined && item.subRows.length > 0) {
                    item.subRows.forEach((subItem: Position, index: number) => {
                        subItem.id = GenerateGUID();
                        subItem.groupId = item.id;
                    });
                }
            });
            state.positionsTabData.positions = newData;

            const posGroup: ListModel<string>[] = [];
            newData.forEach((item: Position, index: number) => {
                if (item.isGroup) {
                    const groupItem: ListModel<string> = {
                        value: item.id,
                        label: item.name
                    };

                    posGroup.push(groupItem);
                }
            });
            state.positionsTabData.positionGroups = posGroup;
        },

        addPositionInList: (state, action: PayloadAction<Position>) => {
            state.positionsTabData.updatePositionsInDb = true;
            if (action.payload.groupId === null) {
                state.positionsTabData.positions.push(action.payload);
            }
            else {
                const parentPosition = state.positionsTabData.positions.find(p => p.id === action.payload.groupId);
                if (parentPosition) {
                    const subRows = parentPosition.subRows || [];
                    subRows.push(action.payload);
                    parentPosition.subRows = subRows;
                }
            }

            if (action.payload.isGroup) {
                const groupItem: ListModel<string> = {
                    value: action.payload.id,
                    label: action.payload.name
                };
                const posGroup: ListModel<string>[] = state.positionsTabData.positionGroups;
                posGroup.push(groupItem);
                state.positionsTabData.positionGroups = posGroup
            }
        },
        updatePositionInList: (state, action: PayloadAction<UpdatePositionDTO>) => {
            const { id, groupId, isGroup, ...updatedPosition } = action.payload.position;
            state.positionsTabData.updatePositionsInDb = true;
            if (groupId === null) {
                if (action.payload.oldGroupId !== null) {
                    const oldParentPosition = state.positionsTabData.positions.find(p => p.id === action.payload.oldGroupId);
                    var oldParentIndex = state.positionsTabData.positions.findIndex(p => p.id === action.payload.oldGroupId);
                    var oldIndex = oldParentPosition?.subRows?.findIndex(p => p.id === id);
                    if (oldParentIndex != -1 && oldIndex !== undefined && oldIndex != -1) {
                        oldParentPosition?.subRows?.splice(oldIndex, 1);
                    }
                    state.positionsTabData.positions.push(action.payload.position);
                    var newData = state.positionsTabData.positions;

                    newData.forEach((item: Position, index: number) => {
                        if (item?.groupId === null) {
                            item.order = index + 1;

                            if (item.subRows != null && item.subRows !== undefined && item.subRows.length > 0) {
                                var i = item.order;
                                item.subRows.forEach((subItem: Position, index: number) => {
                                    subItem.order = Math.round((i + 0.1) * 100) / 100;
                                    i = i + 0.1;
                                });
                            }
                        }
                    });
                    state.positionsTabData.positions = newData;
                }
                else {
                    var index = state.positionsTabData.positions.findIndex(p => p.id === id);
                    if (index !== -1) {
                        state.positionsTabData.positions[index] = action.payload.position;
                    }
                }

            }
            else {
                if (groupId === action.payload.oldGroupId) {
                    var newPositions: Position[] = [];
                    newPositions = state.positionsTabData.positions.map(parentPosition => {
                        if (parentPosition.id === groupId && parentPosition.subRows) {
                            return {
                                ...parentPosition,
                                subRows: parentPosition.subRows.map(subRow =>
                                    subRow.id === id ? { ...subRow, ...updatedPosition } : subRow
                                ),
                            };
                        }
                        return parentPosition;
                    });

                    state.positionsTabData.positions = newPositions;
                }
                else {
                    const oldParentPosition = state.positionsTabData.positions.find(p => p.id === action.payload.oldGroupId);
                    var oldParentIndex = state.positionsTabData.positions.findIndex(p => p.id === action.payload.oldGroupId);
                    var oldIndex = oldParentPosition?.subRows?.findIndex(p => p.id === id);
                    if (oldParentIndex != -1 && oldIndex !== undefined && oldIndex != -1) {
                        oldParentPosition?.subRows?.splice(oldIndex, 1);
                        if (oldParentPosition !== undefined)
                            state.positionsTabData.positions[oldParentIndex] = oldParentPosition
                    }
                    if (action.payload.oldGroupId === null) {
                        const oldPosition = state.positionsTabData.positions.find(p => p.id === id);
                        var oldPositionIndex = state.positionsTabData.positions.findIndex(p => p.id === id);
                        state.positionsTabData.positions.splice(oldPositionIndex, 1);
                    }
                    const newParentPosition = state.positionsTabData.positions.find(p => p.id === groupId);
                    var newParentIndex = state.positionsTabData.positions.findIndex(p => p.id === groupId);
                    newParentPosition?.subRows?.push(action.payload.position);
                    if (newParentPosition !== undefined)
                        state.positionsTabData.positions[newParentIndex] = newParentPosition
                    var newData = state.positionsTabData.positions;

                    newData.forEach((item: Position, index: number) => {
                        if (item?.groupId === null) {
                            item.order = index + 1;

                            if (item.subRows != null && item.subRows !== undefined && item.subRows.length > 0) {
                                var i = item.order;
                                item.subRows.forEach((subItem: Position, index: number) => {
                                    subItem.order = Math.round((i + 0.1) * 100) / 100;
                                    i = i + 0.1;
                                });
                            }
                        }
                    });
                    state.positionsTabData.positions = newData;
                }
            }

            if (isGroup) {
                const groupItem: ListModel<string> = {
                    value: id,
                    label: updatedPosition.name
                };
                var indexGroup = state.positionsTabData.positionGroups.findIndex(p => p.value === id);
                state.positionsTabData.positionGroups[indexGroup] = groupItem;
            }
        },
        removePositionFromList: (state, action: PayloadAction<Position>) => {
            const { id, groupId, isGroup, ...deletedPosition } = action.payload;

            state.positionsTabData.updatePositionsInDb = true;

            if (action.payload.groupId === null) {
                var mainIndex = state.positionsTabData.positions.findIndex(p => p.id === action.payload.id);
                state.positionsTabData.positions.splice(mainIndex, 1);
                if (isGroup) {
                    var groupIndex = state.positionsTabData.positionGroups.findIndex(p => p.value === id);
                    state.positionsTabData.positionGroups.splice(groupIndex, 1);
                }
            }
            else {
                const parentPosition = state.positionsTabData.positions.find(p => p.id === groupId);
                var parentIndex = state.positionsTabData.positions.findIndex(p => p.id === groupId);
                var index: number | undefined = parentPosition?.subRows?.findIndex(p => p.id === id);
                if (parentIndex != -1 && index !== undefined && index != -1) {
                    parentPosition?.subRows?.splice(index, 1);
                    if (parentPosition !== undefined)
                        state.positionsTabData.positions[parentIndex] = parentPosition
                }
            }
            var newData = state.positionsTabData.positions;
            newData.forEach((item: Position, index: number) => {
                if (item?.groupId === null) {
                    item.order = index + 1;

                    if (item.subRows !== null && item.subRows !== undefined && item.subRows.length > 0) {
                        var i = item.order;
                        item.subRows.forEach((subItem: Position, index: number) => {
                            subItem.order = Math.round((i + 0.1) * 100) / 100;
                            i = i + 0.1;
                        });
                    }
                }
            });
            state.positionsTabData.positions = newData;
        },
        resetUpdateReminderPositionsInDb: (state) => {
            state.positionsTabData.updateReminderPositionsInDb = false;
        },
        resetReminderPosition: (state) => {
            state.positionsTabData.reminderPosition = { ...InitReminderPositionModel };
        },
        resetPdfData: (state) => {
            state.tenantPdfData = null;
            state.recipientPdfData = null;
        },
        /* resetPaymentsInReminderPosition: (state, action: PayloadAction<string>) => {
            const reminderPositions = [...state.positionsTabData.reminderPositions];
            const totalPaidAmount = state.invoiceAndPaymentDetails?.paidAmount ?? 0;

            if (!reminderPositions || reminderPositions.length === 0) return;

            reminderPositions.sort((a, b) => new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime());
            const updatedPositions: ReminderPosition[] = reminderPositions.map(pos => ({ ...pos, paymentAmount: 0 }));

            if(action.payload === 'main'){
                const oldest = updatedPositions[0];
                if (oldest) {
                    oldest.paymentAmount = totalPaidAmount;
                }
            }
            else if(action.payload === 'distribute'){
                let remaining = totalPaidAmount;

                for (const pos of updatedPositions) {
                    const invoiceAmount = pos.invoiceAmount ?? 0;

                    if (remaining >= invoiceAmount) {
                        pos.paymentAmount = invoiceAmount;
                        remaining -= invoiceAmount;
                    } else {
                        pos.paymentAmount = remaining;
                        remaining = 0;
                        break;
                    }
                }   

                // If any remaining, add to last (main) position
                if (remaining > 0) {
                    const last = updatedPositions[updatedPositions.length - 1];
                    last.paymentAmount += remaining;
                }
            }

            state.positionsTabData.reminderPositions = updatedPositions;
        }, */
        setReminderPosition: (state, action: PayloadAction<ReminderPosition>) => {
            state.positionsTabData.reminderPosition = action.payload;
        },
        setNewReminderPosition: (state) => {
            var dateString: string = state.invoiceAndPaymentDetails.invoiceDate !== null ? "_" + parseUnlessDate(state.invoiceAndPaymentDetails.invoiceDate, 'YYYY-MM-DD').tz(getUserTimeZone()).format('YYMMDD') : "";
            var documentNumberString: string = state.invoiceAndPaymentDetails.invoiceNumber !== null ? "_" + state.invoiceAndPaymentDetails.invoiceNumber : "";
            var positionName: string = state.invoiceAndPaymentDetails.tenantAbbreviation + dateString + documentNumberString;
            var newReminderPosition: ReminderPosition = { id: GenerateGUID(), name: positionName, order: 1, invoiceDate: state.invoiceAndPaymentDetails.invoiceDate!, invoiceAmount: state.invoiceAndPaymentDetails.invoiceAmount, paymentAmount: state.invoiceAndPaymentDetails.paidAmount ?? 0 };
            state.positionsTabData.reminderPositions.push(newReminderPosition);
        },
        setDunningPositions: (state) => {
            var newData: ReminderPosition[] = [];
            var dateString: string = state.invoiceAndPaymentDetails.invoiceDate !== null ? "_" + parseUnlessDate(state.invoiceAndPaymentDetails.invoiceDate, 'YYYY-MM-DD').tz(getUserTimeZone()).format('YYMMDD') : "";
            var documentNumberString: string = state.invoiceAndPaymentDetails.invoiceNumber !== null ? "_" + state.invoiceAndPaymentDetails.invoiceNumber : "";
            var positionName: string = state.invoiceAndPaymentDetails.tenantAbbreviation + dateString + documentNumberString;
            var newReminderPosition: ReminderPosition = { id: GenerateGUID(), name: positionName, order: 1, invoiceDate: state.invoiceAndPaymentDetails.invoiceDate!, invoiceAmount: state.invoiceAndPaymentDetails.invoiceAmount, paymentAmount: state.invoiceAndPaymentDetails.paidAmount };
            newData.push(newReminderPosition);
            state.positionsTabData.reminderPositions = newData;
        },
        generateNewIdsForDunningPositions: (state) => {
            var newData = state.positionsTabData.reminderPositions;
            newData.forEach((item: ReminderPosition, index: number) => {
                item.id = GenerateGUID();

            });
            state.positionsTabData.reminderPositions = newData;
        },
        setNewReminderPositionIds: (state) => {
            var newData = state.positionsTabData.reminderPositions;
            newData.forEach((item: ReminderPosition, index: number) => {
                item.id = GenerateGUID();
            });
            state.positionsTabData.reminderPositions = newData;
        },
        setReminderPositions: (state, action: PayloadAction<ReminderPosition[]>) => {
            state.positionsTabData.updateReminderPositionsInDb = true;
            state.positionsTabData.reminderPositions = action.payload;
        },
        addReminderPositionInList: (state, action: PayloadAction<ReminderPosition>) => {
            state.positionsTabData.updateReminderPositionsInDb = true;
            state.positionsTabData.reminderPositions.push(action.payload);
        },
        updateReminderPositionInList: (state, action: PayloadAction<ReminderPosition>) => {
            var index = state.positionsTabData.reminderPositions.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.positionsTabData.reminderPositions[index] = action.payload;
            }
            state.positionsTabData.updateReminderPositionsInDb = true;
        },
        removeReminderPositionFromList: (state, action: PayloadAction<ReminderPosition>) => {
            var mainIndex = state.positionsTabData.reminderPositions.findIndex(p => p.id === action.payload.id);
            state.positionsTabData.reminderPositions.splice(mainIndex, 1);

            var newData = state.positionsTabData.reminderPositions;
            newData.forEach((item: ReminderPosition, index: number) => {
                item.order = index + 1;
            });

            state.positionsTabData.reminderPositions = newData;
            state.positionsTabData.updateReminderPositionsInDb = true;
        },
        setFinanceDocumentModelValidity: (state, action: PayloadAction<boolean>) => {
            state.financeDocumentCreateModelValid = action.payload;
        },
        setFinanceDocumentType: (state, action: PayloadAction<string>) => {
            state.financeDocumentTypeId = action.payload;
        },
        setAdditionalCurrency: (state, action: PayloadAction<string>) => {
            state.pdfTabData.additionalPdfData.additionalCurrency = action.payload;
        },
        setExchangeRate: (state, action: PayloadAction<number | null>) => {
            state.pdfTabData.additionalPdfData.exchangeRate = action.payload;
        },
        setPdfLanguage: (state, action: PayloadAction<string>) => {
            state.pdfTabData.additionalPdfData.language = action.payload;
        },
        setAdditionalPdfLanguage: (state, action: PayloadAction<string>) => {
            state.pdfTabData.additionalPdfData.additionalLanguage = action.payload;
        },
        setShowArticle: (state, action: PayloadAction<boolean>) => {
            state.pdfTabData.additionalPdfData.showArticle = action.payload;
        },
        setShowWage: (state, action: PayloadAction<boolean>) => {
            state.pdfTabData.additionalPdfData.showWage = action.payload;
        },
        setShowMaterial: (state, action: PayloadAction<boolean>) => {
            state.pdfTabData.additionalPdfData.showMaterial = action.payload;
        },
        setPdfProcessingState: (state, action: PayloadAction<boolean>) => {
            state.pdfTabData.isCreatePdfProcessing = action.payload;
        },
        // resetDocumentForDownload: (state) => {
        //     state.pdfTabData.documentToDownload = null;
        // },
        // addProcedureFile: (state, action: PayloadAction<ProcedureInfoFile>) => {
        //     let files = [...state.procedureInfoTabData.procedureInfo.files];
        //     const newFile = action.payload;

        //     if (newFile.isMain) {
        //         files.forEach(file => {
        //             if (file.isMain) {
        //                 file.isMain = false;
        //             }
        //         });
        //     }

        //     if (newFile.parentFileId) {
        //         const parentFile = files.find(file => file.id === newFile.parentFileId);

        //         if (parentFile) {
        //             if (!parentFile.additionalFiles) {
        //                 parentFile.additionalFiles = [];
        //             }
        //             parentFile.additionalFiles.push(newFile);
        //         }
        //     } else {
        //         files.push(newFile);
        //     }

        //     state.procedureInfoTabData.procedureInfo.files = files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
        // },
        // deleteProcedureFile: (state, action: PayloadAction<string>) => {
        //     const recursiveDelete = (files: ProcedureInfoFile[], id: string): boolean => {
        //         for (let i = 0; i < files.length; i++) {
        //             const file = files[i];

        //             if (file.id === id) {
        //                 files.splice(i, 1);
        //                 return true;
        //             }

        //             if (file.additionalFiles && recursiveDelete(file.additionalFiles, id)) {
        //                 return true;
        //             }
        //         }
        //         return false;
        //     };

        //     const filesToUpdate = [...state.procedureInfoTabData.procedureInfo.files];
        //     recursiveDelete(filesToUpdate, action.payload);
        //     filesToUpdate.sort((a, b) => Number(b.isMain) - Number(a.isMain));
        //     state.procedureInfoTabData.procedureInfo.files = filesToUpdate;
        // },
        // deleteProcedureFiles: (state) => {
        //     state.procedureInfoTabData.procedureInfo.files = [];
        // },
        // updateProcedureFile: (state, action: PayloadAction<ProcedureInfoFile>) => {
        //     const recursiveUpdate = (files: ProcedureInfoFile[], updatedFile: ProcedureInfoFile): boolean => {
        //         for (let i = 0; i < files.length; i++) {
        //             const file = files[i];

        //             if (file.id === updatedFile.id) {
        //                 if (updatedFile.isMain) {
        //                     state.procedureInfoTabData.procedureInfo.files.forEach(f => f.isMain = false);
        //                 }
        //                 files[i] = updatedFile;
        //                 return true;
        //             }

        //             if (file.additionalFiles && recursiveUpdate(file.additionalFiles, updatedFile)) {
        //                 return true;
        //             }
        //         }
        //         return false;
        //     };

        //     const filesToUpdate = [...state.procedureInfoTabData.procedureInfo.files];
        //     recursiveUpdate(filesToUpdate, action.payload);
        //     filesToUpdate.sort((a, b) => Number(b.isMain) - Number(a.isMain));
        //     state.procedureInfoTabData.procedureInfo.files = filesToUpdate;
        // },
        // downloadProcedureFile: (state, action: PayloadAction<string>) => {
        //     const recursiveFindFile = (files: ProcedureInfoFile[], id: string): ProcedureInfoFile | null => {
        //         for (const file of files) {
        //             if (file.id === id) {
        //                 return file;
        //             }
        //             if (file.additionalFiles) {
        //                 const foundFile = recursiveFindFile(file.additionalFiles, id);
        //                 if (foundFile) {
        //                     return foundFile;
        //                 }
        //             }
        //         }
        //         return null;
        //     };

        //     const files = state.procedureInfoTabData.procedureInfo.files;
        //     const fileToDownload = recursiveFindFile(files, action.payload);

        //     if (fileToDownload) {
        //         state.procedureInfoTabData.procedureFileForDownload = fileToDownload;
        //     }
        // },
        resetDocumentVersionsHistory: (state) => {
            state.pdfTabData.documentFiles = [];
        }
    },
    extraReducers(builder) {
        //#region Request cases
        //Get All Active Requests
        builder
            .addCase(GetAllActiveRequests.fulfilled, (state, action: PayloadAction<GetFinanceRequestsResponse>) => {
                state.documentLists.activeRequests = action.payload.requests;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive Requests
        builder
            .addCase(GetAllInactiveRequests.fulfilled, (state, action: PayloadAction<GetFinanceRequestsResponse>) => {
                state.documentLists.inactiveRequests = action.payload.requests;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Request By Id
        builder
            .addCase(GetRequestById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(GetRequestById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetRequestById.pending, state => {
                state.isModalLoading = true;
            })
        //Send request to approval
        builder
            .addCase(SendRequestToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                };

                if (action.payload.projectSelectListItem.length > 0) {
                    if (state.dropdownLists && state.dropdownLists.projects) {
                        const item: ListModel<string> = action.payload.projectSelectListItem[0];
                        const exists = (state.dropdownLists.projects)
                            .some(p => p.value === item.value);

                        if (!exists) {
                            (state.dropdownLists.projects as ListModel<string>[]).push(item);
                        }
                    }
                };
            })
            .addCase(SendRequestToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendRequestToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Request
        builder
            .addCase(CreateRequest.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The request is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }

                if (action.payload.documentNumber) {
                    state.relatedFinanceDocumentNumbers.requestNumber = action.payload.documentNumber;
                }
            })
            .addCase(CreateRequest.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateRequest.pending, state => {
                state.isModalLoading = true;
            })
        //Update Request Basic info
        builder
            .addCase(UpdateRequestBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The request basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
                if (action.payload.documentNumber) {
                    state.relatedFinanceDocumentNumbers.requestNumber = action.payload.documentNumber;
                }
            })
            .addCase(UpdateRequestBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateRequestBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Request status
        builder
            .addCase(UpdateRequestStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The request status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateRequestStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateRequestStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Request Positions
        builder
            .addCase(UpdateRequestPositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateRequestPositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateRequestPositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Request Textblocks
        builder
            .addCase(UpdateRequestTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateRequestTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateRequestTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Request File
        builder
            .addCase(DownloadRequestFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadRequestFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadRequestFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Request File Upload
        builder
            .addCase(RequestFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(RequestFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(RequestFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Request File
        builder
            .addCase(DeleteRequestFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The request file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteRequestFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteRequestFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateRequestPdfDocument
        builder
            .addCase(CreateRequestPdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The request document version is successfully created.';
                state.statusCode = '200';
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateRequestPdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateRequestPdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Offer cases
        //Get All Active Offers
        builder
            .addCase(GetAllActiveOffers.fulfilled, (state, action: PayloadAction<GetOffersResponse>) => {
                state.documentLists.activeOffers = action.payload.offers;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive Offers
        builder
            .addCase(GetAllInactiveOffers.fulfilled, (state, action: PayloadAction<GetOffersResponse>) => {
                state.documentLists.inactiveOffers = action.payload.offers;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Offer By Id
        builder
            .addCase(GetOfferById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(GetOfferById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetOfferById.pending, state => {
                state.isModalLoading = true;
            })
        //Send offer to approval
        builder
            .addCase(SendOfferToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendOfferToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendOfferToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Offer
        builder
            .addCase(CreateOffer.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The offer is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateOffer.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateOffer.pending, state => {
                state.isModalLoading = true;
            })
        //Update Offer Basic info
        builder
            .addCase(UpdateOfferBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The offer basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateOfferBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateOfferBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Offer status
        builder
            .addCase(UpdateOfferStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The offer status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateOfferStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateOfferStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Offer Positions
        builder
            .addCase(UpdateOfferPositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateOfferPositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateOfferPositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Offer Textblocks
        builder
            .addCase(UpdateOfferTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateOfferTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateOfferTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Offer File
        builder
            .addCase(DownloadOfferFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadOfferFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadOfferFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Offer File Upload
        builder
            .addCase(OfferFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(OfferFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(OfferFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Offer File
        builder
            .addCase(DeleteOfferFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The offer file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteOfferFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteOfferFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateOfferPdfDocument
        builder
            .addCase(CreateOfferPdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The offer document version is successfully created.';
                state.statusCode = '200';
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOfferPdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOfferPdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Order cases
        //Get All Active Orders
        builder
            .addCase(GetAllActiveOrders.fulfilled, (state, action: PayloadAction<GetOrdersResponse>) => {
                state.documentLists.activeOrders = action.payload.orders;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive Orders
        builder
            .addCase(GetAllInactiveOrders.fulfilled, (state, action: PayloadAction<GetOrdersResponse>) => {
                state.documentLists.inactiveOrders = action.payload.orders;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Order By Id
        builder
            .addCase(GetOrderById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(GetOrderById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetOrderById.pending, state => {
                state.isModalLoading = true;
            })
        //Send order to approval
        builder
            .addCase(SendOrderToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendOrderToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendOrderToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Order
        builder
            .addCase(CreateOrder.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The order is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateOrder.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateOrder.pending, state => {
                state.isModalLoading = true;
            })
        //Update Order Basic info
        builder
            .addCase(UpdateOrderBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The order basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateOrderBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateOrderBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Order status
        builder
            .addCase(UpdateOrderStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The order status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateOrderStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateOrderStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Order Positions
        builder
            .addCase(UpdateOrderPositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateOrderPositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateOrderPositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Order Textblocks
        builder
            .addCase(UpdateOrderTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateOrderTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateOrderTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Order File
        builder
            .addCase(DownloadOrderFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadOrderFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadOrderFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Order File Upload
        builder
            .addCase(OrderFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(OrderFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(OrderFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Order File
        builder
            .addCase(DeleteOrderFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The order file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteOrderFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteOrderFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateOrderPdfDocument
        builder
            .addCase(CreateOrderPdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The order document version is successfully created.';
                state.statusCode = "200";
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOrderPdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOrderPdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Order Confirmation
        //Get All Active Order Confirmations
        builder
            .addCase(GetAllActiveOrderConfirmations.fulfilled, (state, action: PayloadAction<GetOrderConfirmationsResponse>) => {
                state.documentLists.activeOrderConfirmations = action.payload.orderConfirmations;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
                state.permissions.createNextTenants = action.payload.createNextTenants;
            })
        //Get All Inactive Order Confirmations
        builder
            .addCase(GetAllInactiveOrderConfirmations.fulfilled, (state, action: PayloadAction<GetOrderConfirmationsResponse>) => {
                state.documentLists.inactiveOrderConfirmations = action.payload.orderConfirmations;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
                state.permissions.createNextTenants = action.payload.createNextTenants;
            })
        //Get Order Confirmation By Id
        builder
            .addCase(GetOrderConfirmationById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.pdfTabData.documentFiles = action.payload.documents;
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(GetOrderConfirmationById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetOrderConfirmationById.pending, state => {
                state.isModalLoading = true;
            })
        //Send Order confirmation to approval
        builder
            .addCase(SendOrderConfirmationToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendOrderConfirmationToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendOrderConfirmationToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Order Confirmation
        builder
            .addCase(CreateOrderConfirmation.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The order confirmation is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateOrderConfirmation.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateOrderConfirmation.pending, state => {
                state.isModalLoading = true;
            })
        //Update Order Confirmation Basic info
        builder
            .addCase(UpdateOrderConfirmationBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The order confirmation basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateOrderConfirmationBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateOrderConfirmationBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update OrderConfirmation status
        builder
            .addCase(UpdateOrderConfirmationStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The order confirmation status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateOrderConfirmationStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateOrderConfirmationStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Order Confirmation Positions
        builder
            .addCase(UpdateOrderConfirmationPositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateOrderConfirmationPositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateOrderConfirmationPositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Order Confirmation Textblocks
        builder
            .addCase(UpdateOrderConfirmationTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateOrderConfirmationTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateOrderConfirmationTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Order Confirmation File
        builder
            .addCase(DownloadOrderConfirmationFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadOrderConfirmationFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadOrderConfirmationFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Order Confirmation File Upload
        builder
            .addCase(OrderConfirmationFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(OrderConfirmationFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(OrderConfirmationFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Order Confirmation File
        builder
            .addCase(DeleteOrderConfirmationFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The order confirmation file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteOrderConfirmationFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteOrderConfirmationFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateOrderConfirmationPdfDocument
        builder
            .addCase(CreateOrderConfirmationPdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The order confirmation document version is successfully created.';
                state.statusCode = "200";
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOrderConfirmationPdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateOrderConfirmationPdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Invoice cases
        //Get All Active Invoices
        builder
            .addCase(GetAllActiveInvoices.fulfilled, (state, action: PayloadAction<GetInvoicesResponse>) => {
                state.documentLists.activeInvoices = action.payload.invoices;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive Invoices
        builder
            .addCase(GetAllInactiveInvoices.fulfilled, (state, action: PayloadAction<GetInvoicesResponse>) => {
                state.documentLists.inactiveInvoices = action.payload.invoices;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Invoice By Id
        builder
            .addCase(GetInvoiceById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                if (!state.invoiceAndPaymentDetails) {
                    state.invoiceAndPaymentDetails = {
                        currency: '',
                        invoiceAmount: 0,
                        paidAmount: 0,
                        tenantAbbreviation: "",
                        invoiceId: action.payload.basicInfo.id,
                        invoiceDate: action.payload.basicInfo.documentDate ?? null,
                        invoiceNumber: action.payload.basicInfo.documentNumber
                    };

                }
                else {
                    state.invoiceAndPaymentDetails.invoiceDate = action.payload.basicInfo.documentDate ?? null;
                    state.invoiceAndPaymentDetails.invoiceNumber = action.payload.basicInfo.documentNumber;
                    state.invoiceAndPaymentDetails.invoiceId = action.payload.basicInfo.id;
                }

                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }

                state.pdfTabData.documentFiles = action.payload.documents;
            })
            .addCase(GetInvoiceById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInvoiceById.pending, state => {
                state.isModalLoading = true;
            })
        //Get Invoice and paymnet sum
        builder
            .addCase(GetInvoiceAndPaymentSum.fulfilled, (state, action: PayloadAction<InvoceAndPaymentSumAmountResponse>) => {
                if (!state.invoiceAndPaymentDetails) {
                    state.invoiceAndPaymentDetails = {
                        currency: action.payload.currency,
                        invoiceAmount: action.payload.invoiceAmount,
                        paidAmount: action.payload.paidAmount,
                        tenantAbbreviation: action.payload.tenantAbbreviation,
                        invoiceId: '',
                        invoiceDate: null,
                        invoiceNumber: null,
                    };

                }
                else {
                    state.invoiceAndPaymentDetails.currency = action.payload.currency;
                    state.invoiceAndPaymentDetails.invoiceAmount = action.payload.invoiceAmount;
                    state.invoiceAndPaymentDetails.paidAmount = action.payload.paidAmount;
                    state.invoiceAndPaymentDetails.tenantAbbreviation = action.payload.tenantAbbreviation;
                }
                state.isModalLoading = false;
            })
            .addCase(GetInvoiceAndPaymentSum.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInvoiceAndPaymentSum.pending, state => {
                state.isModalLoading = true;
            })
        //Send inovice to approval
        builder
            .addCase(SendInvoiceToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendInvoiceToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendInvoiceToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Invoice
        builder
            .addCase(CreateInvoice.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The invoice is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateInvoice.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateInvoice.pending, state => {
                state.isModalLoading = true;
            })
        //Update Invoice Basic info
        builder
            .addCase(UpdateInvoiceBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The invoice basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateInvoiceBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateInvoiceBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Invoice status
        builder
            .addCase(UpdateInvoiceStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The invoice status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateInvoiceStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateInvoiceStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Invoice Positions
        builder
            .addCase(UpdateInvoicePositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateInvoicePositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateInvoicePositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Invoice Textblocks
        builder
            .addCase(UpdateInvoiceTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateInvoiceTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateInvoiceTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Invoice File
        builder
            .addCase(DownloadInvoiceFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadInvoiceFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadInvoiceFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Invoice File Upload
        builder
            .addCase(InvoiceFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(InvoiceFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(InvoiceFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Invoice File
        builder
            .addCase(DeleteInvoiceFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The invoice file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteInvoiceFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteInvoiceFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateInvoicePdfDocument
        builder
            .addCase(CreateInvoicePdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The invoice document version is successfully created.';
                state.statusCode = "200";
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateInvoicePdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateInvoicePdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //Create Invoice Payment Data
        builder
            .addCase(CreateUpdateInvoicePaymentData.fulfilled, (state, action: PayloadAction<PaymentDataResponseWrapper>) => {
                state.isModalLoading = false;
                state.message = action.payload.message ?? 'The message cannot be displayed.';
                state.statusCode = "200";
                state.showNotification = true;
            })
            .addCase(CreateUpdateInvoicePaymentData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateUpdateInvoicePaymentData.pending, state => {
                state.isModalLoading = true;
            })
        //Get Invoice Payment Data
        builder
            .addCase(GetInvoicePaymentData.fulfilled, (state, action: PayloadAction<PaymentDataResponseWrapper>) => {
                state.isModalLoading = false;
                // state.statusCode = "200";
                state.paymentsTabData = action.payload.invoicePaymentData;
            })
            .addCase(GetInvoicePaymentData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInvoicePaymentData.pending, state => {
                //state.isModalLoading = true;
            })
        //Get Export File Data
        builder
            .addCase(GetExportFileData.fulfilled, (state, action: PayloadAction<ExportFileDataResponse>) => {
                state.isModalLoading = false;
                state.exportFileDataTab.exportFileData = action.payload;
            })
            .addCase(GetExportFileData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetExportFileData.pending, state => {
                state.isModalLoading = true;
            })
        //Send Export File Data
        builder
            .addCase(SendExportFileData.fulfilled, (state, action: PayloadAction<ExportFileResponse>) => {
                state.isLoading = false;
                state.exportFileDataTab.exportFile = action.payload;
            })
            .addCase(SendExportFileData.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendExportFileData.pending, state => {
                // state.isLoading = true;
            })
        //#endregion

        //#region Credit Note cases
        //Get All Active CreditNotes
        builder
            .addCase(GetAllActiveCreditNotes.fulfilled, (state, action: PayloadAction<GetCreditNotesResponse>) => {
                state.documentLists.activeCreditNotes = action.payload.creditNotes;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive CreditNotes
        builder
            .addCase(GetAllInactiveCreditNotes.fulfilled, (state, action: PayloadAction<GetCreditNotesResponse>) => {
                state.documentLists.inactiveCreditNotes = action.payload.creditNotes;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Credit Note By Id
        builder
            .addCase(GetCreditNoteById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                // TODO: Check if needed
                // if (!state.invoiceAndPaymentDetails) {
                //     state.invoiceAndPaymentDetails = {
                //         currency: '',
                //         invoiceAmount: 0,
                //         paidAmount: 0,
                //         tenantAbbreviation: "",
                //         invoiceId: action.payload.basicInfo.id,
                //         invoiceDate: action.payload.basicInfo.documentDate ?? null,
                //         invoiceNumber: action.payload.basicInfo.documentNumber
                //     };

                // }
                // else {
                //     state.invoiceAndPaymentDetails.invoiceDate = action.payload.basicInfo.documentDate ?? null;
                //     state.invoiceAndPaymentDetails.invoiceNumber = action.payload.basicInfo.documentNumber;
                //     state.invoiceAndPaymentDetails.invoiceId = action.payload.basicInfo.id;
                // }

                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }

                state.pdfTabData.documentFiles = action.payload.documents;
            })
            .addCase(GetCreditNoteById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCreditNoteById.pending, state => {
                state.isModalLoading = true;
            })
        //Get Credit Note and payment sum
        builder
            .addCase(GetCreditNoteAndPaymentSum.fulfilled, (state, action: PayloadAction<InvoceAndPaymentSumAmountResponse>) => {
                // TODO: Check if needed
                // if (!state.invoiceAndPaymentDetails) {
                //     state.invoiceAndPaymentDetails = {
                //         currency: action.payload.currency,
                //         invoiceAmount: action.payload.invoiceAmount,
                //         paidAmount: action.payload.paidAmount,
                //         tenantAbbreviation: action.payload.tenantAbbreviation,
                //         invoiceId: '',
                //         invoiceDate: null,
                //         invoiceNumber: null,
                //     };

                // }
                // else {
                //     state.invoiceAndPaymentDetails.currency = action.payload.currency;
                //     state.invoiceAndPaymentDetails.invoiceAmount = action.payload.invoiceAmount;
                //     state.invoiceAndPaymentDetails.paidAmount = action.payload.paidAmount;
                //     state.invoiceAndPaymentDetails.tenantAbbreviation = action.payload.tenantAbbreviation;
                // }
                state.isModalLoading = false;
            })
            .addCase(GetCreditNoteAndPaymentSum.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCreditNoteAndPaymentSum.pending, state => {
                state.isModalLoading = true;
            })
        //Send inovice to approval
        builder
            .addCase(SendCreditNoteToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.statusCode = '200';
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.pdfTabData.documentFiles = action.payload.documents;
                state.positionsTabData.positions = action.payload.positions as Position[];
                state.positionsTabData.lastValidPositions = action.payload.positions as Position[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendCreditNoteToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendCreditNoteToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create CreditNote
        builder
            .addCase(CreateCreditNote.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The credit note is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateCreditNote.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateCreditNote.pending, state => {
                state.isModalLoading = true;
            })
        //Update Credit Note Basic info
        builder
            .addCase(UpdateCreditNoteBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The credit note basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateCreditNoteBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateCreditNoteBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Credit Note status
        builder
            .addCase(UpdateCreditNoteStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The credit note status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateCreditNoteStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateCreditNoteStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Credit Note Positions
        builder
            .addCase(UpdateCreditNotePositions.fulfilled, (state, action: PayloadAction<UpdatePositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.positionsTabData.updatePositionsInDb = false;
                state.positionsTabData.positions = action.payload.positions;
                state.positionsTabData.lastValidPositions = action.payload.positions;
                var newGroup: ListModel<string>[] = [];
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        newGroup.push(groupItem);
                    }
                });
                state.positionsTabData.positionGroups = newGroup;
            })
            .addCase(UpdateCreditNotePositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.positions = state.positionsTabData.lastValidPositions;
                state.positionsTabData.positions.forEach(pos => {
                    if (pos.isGroup === true) {
                        const groupItem: ListModel<string> = {
                            value: pos.id,
                            label: pos.name
                        };
                        state.positionsTabData.positionGroups.push(groupItem);
                    }
                });
                state.showNotification = true;
            })
            .addCase(UpdateCreditNotePositions.pending, state => {
                //state.isModalLoading = true;
            })
        //Update Credit Note Textblocks
        builder
            .addCase(UpdateCreditNoteTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateCreditNoteTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateCreditNoteTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Credit Note File
        builder
            .addCase(DownloadCreditNoteFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadCreditNoteFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadCreditNoteFile.pending, state => {
                //state.isModalLoading = true;
            })
        //CreditNote File Upload
        builder
            .addCase(CreditNoteFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(CreditNoteFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreditNoteFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Credit Note File
        builder
            .addCase(DeleteCreditNoteFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The credit note file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteCreditNoteFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteCreditNoteFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateCreditNotePdfDocument
        builder
            .addCase(CreateCreditNotePdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The credit note document version is successfully created.';
                state.statusCode = "200";
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateCreditNotePdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateCreditNotePdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Reminder and Dunning cases
        //Get All Active Reminders
        builder
            .addCase(GetAllActiveReminders.fulfilled, (state, action: PayloadAction<GetRemindersResponse>) => {
                state.documentLists.activeReminders = action.payload.reminders;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get All Inactive Reminders
        builder
            .addCase(GetAllInactiveReminders.fulfilled, (state, action: PayloadAction<GetRemindersResponse>) => {
                state.documentLists.inactiveReminders = action.payload.reminders;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
        //Get Reminder By Id
        builder
            .addCase(GetReminderById.fulfilled, (state, action: PayloadAction<FinanceDocumentResponse>) => {
                state.isModalLoading = false;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.reminderPositions = action.payload.positions as ReminderPosition[];
                state.positionsTabData.lastValidReminderPositions = action.payload.positions as ReminderPosition[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;
                if (action.payload.pdfSettings !== null) {
                    state.pdfTabData.additionalPdfData = action.payload.pdfSettings;
                }
                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(GetReminderById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetReminderById.pending, state => {
                state.isModalLoading = true;
            })
        //Send Reminder to approval
        builder
            .addCase(SendReminderToApproval.fulfilled, (state, action: PayloadAction<FinanceDocumentApprovalResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'The document is successfully sent to approval.';
                state.statusCode = '200';
                state.showNotification = true;
                state.financeDocumentId = action.payload.basicInfo.id;
                state.basicInfoTabData = action.payload.basicInfo;
                state.procedureInfoTabData.procedureInfo = action.payload.procedure;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.positionsTabData.reminderPositions = action.payload.positions as ReminderPosition[];
                state.positionsTabData.lastValidReminderPositions = action.payload.positions as ReminderPosition[];
                state.financeDocumentRequestStatusId = action.payload.requestStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;

                state.relatedFinanceDocumentNumbers = action.payload.relatedFinanceDocumentNumbers;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: action.payload.basicInfo,
                    procedureInfo: action.payload.procedure
                }
            })
            .addCase(SendReminderToApproval.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SendReminderToApproval.pending, state => {
                state.isModalLoading = true;
            })
        //Create Reminder
        builder
            .addCase(CreateReminder.fulfilled, (state, action: PayloadAction<CreateFinanceDocumentResponse>) => {
                state.message = 'The reminder is successfully created.';
                state.statusCode = "200";
                state.financeDocumentId = action.payload.id.toString();
                state.basicInfoTabData.id = action.payload.id.toString();
                // state.isModalLoading = false;
                state.showNotification = true;

                state.financeDocumentDatabaseState = {
                    basicInfoTabData: { ...state.basicInfoTabData, id: action.payload.id.toString() },
                    procedureInfo: state.procedureInfoTabData.procedureInfo
                }
            })
            .addCase(CreateReminder.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateReminder.pending, state => {
                state.isModalLoading = true;
            })
        //Update Reminder Basic info
        builder
            .addCase(UpdateReminderBasicInfo.fulfilled, (state, action: PayloadAction<UpdateBasicInfoResponse>) => {
                state.message = 'The reminder basic info is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData = { ...state.basicInfoTabData, statusId: action.payload.documentStatusId }
            })
            .addCase(UpdateReminderBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateReminderBasicInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Update Reminder status
        builder
            .addCase(UpdateReminderStatus.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The reminder status is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                // state.showNotification = true;
                state.pdfTabData.isFirstPdfCreated = false;
            })
            .addCase(UpdateReminderStatus.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // state.showNotification = true;
            })
            .addCase(UpdateReminderStatus.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Reminder Positions
        builder
            .addCase(UpdateReminderPositions.fulfilled, (state, action: PayloadAction<UpdateReminderPositionResponse>) => {
                state.message = 'Positions are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.positionsTabData.updateReminderPositionsInDb = false;
                state.positionsTabData.reminderPositions = action.payload.reminderPositions;
                state.positionsTabData.lastValidReminderPositions = action.payload.reminderPositions;
            })
            .addCase(UpdateReminderPositions.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.positionsTabData.reminderPositions = state.positionsTabData.lastValidReminderPositions;
                state.showNotification = true;
            })
            .addCase(UpdateReminderPositions.pending, state => {
                //state.isModalLoading = true;
            })

        //Update Reminder Textblocks
        builder
            .addCase(UpdateReminderTextblocks.fulfilled, (state, action: PayloadAction<UpdateTextBlocksResponse>) => {
                state.message = 'Text blocks are successfully updated.';
                state.statusCode = "200";
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;

                var sortedTextblocks = action.payload.textBlocks.sort((a, b) => {
                    if (a.positionId !== b.positionId) {
                        return a.positionId - b.positionId;
                    }
                    return a.orderInPosition - b.orderInPosition;
                });

                state.textblockTabData.selectedTextblocks = sortedTextblocks;
                state.textblockTabData.lastValidData = sortedTextblocks;
            })
            .addCase(UpdateReminderTextblocks.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.textblockTabData.selectedTextblocks = state.textblockTabData.lastValidData;
            })
            .addCase(UpdateReminderTextblocks.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Reminder File
        builder
            .addCase(DownloadReminderFile.fulfilled, (state, action: PayloadAction<DownloadFileResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
            })
            .addCase(DownloadReminderFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadReminderFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Reminder File Upload
        builder
            .addCase(ReminderFileUpload.fulfilled, (state, action: PayloadAction<FileUplaodResponse>) => {
                state.message = 'The file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.attachmentsTabData.attachments = action.payload.files.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
            })
            .addCase(ReminderFileUpload.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReminderFileUpload.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Reminder File
        builder
            .addCase(DeleteReminderFile.fulfilled, (state, action: PayloadAction<FileDeleteResponse>) => {
                state.message = 'The file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
            })
            .addCase(DeleteReminderFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteReminderFile.pending, state => {
                state.isModalLoading = true;
            })
        //CreateReminderPdfDocument
        builder
            .addCase(CreateReminderPdfDocument.fulfilled, (state, action: PayloadAction<CreatePdfResponse>) => {
                state.isModalLoading = false;
                state.message = 'The document version is successfully created.';
                state.statusCode = '200';
                state.showNotification = true;
                state.pdfTabData.documentFiles = action.payload.documents;
                if (action.payload.documents.length === 1) {
                    state.pdfTabData.isFirstPdfCreated = true;
                }
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateReminderPdfDocument.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
            })
            .addCase(CreateReminderPdfDocument.pending, state => {
                //state.isModalLoading = true;
            })
        //#endregion

        //#region Contract list
        //Get contract list items
        builder
            .addCase(GetAllContractListItems.fulfilled, (state, action: PayloadAction<ContractListItem[]>) => {
                state.isLoading = false;
                state.message = '';
                state.documentLists.contractList = action.payload;
            })
            .addCase(GetAllContractListItems.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllContractListItems.pending, state => {
                state.isLoading = true;
            })
        //#endregion

        //#region Shared cases
        //Get Pdf Data
        builder
            .addCase(GetPdfData.fulfilled, (state, action: PayloadAction<PdfDataResponse>) => {
                state.tenantPdfData = action.payload.tenantData;
                state.recipientPdfData = action.payload.recipientData;
                state.pdfTabData.isCostCenter = action.payload.isCostCenter;
            })
            .addCase(GetPdfData.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPdfData.pending, state => {
            })
        //Get Create Data
        builder
            .addCase(GetCreateData.fulfilled, (state, action: PayloadAction<FinanceDocumentDropdownLists>) => {
                state.isModalLoading = false;
                state.dropdownLists = action.payload;
                state.dropdownLists.statuses = action.payload.statuses?.sort((a, b) => {
                    return a.value! - b.value!
                })
            })
            .addCase(GetCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCreateData.pending, state => {
                state.isModalLoading = true;
            })
        //Get filtered partners and tenants list
        builder.addCase(GetFilteredPartnersAndTenantsForDropdown.pending, (state) => {
            state.filteredPartnersAndTenantsList.loading = true;
            state.filteredPartnersAndTenantsList.error = null;
        })
            .addCase(GetFilteredPartnersAndTenantsForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersAndTenantsList.loading = false;
                state.filteredPartnersAndTenantsList.data = action.payload.data;
                state.filteredPartnersAndTenantsList.totalItems = action.payload.totalItems;
                state.filteredPartnersAndTenantsList.totalPages = action.payload.totalPages;
                state.filteredPartnersAndTenantsList.currentPage = action.payload.currentPage;
            })
            .addCase(GetFilteredPartnersAndTenantsForDropdown.rejected, (state, action) => {
                state.filteredPartnersAndTenantsList.loading = false;
                state.filteredPartnersAndTenantsList.error = action.payload?.message || 'Error fetching data';
            });
        //Get Project Data
        builder
            .addCase(GetProjectData.fulfilled, (state, action: PayloadAction<GetProjectDataResponse>) => {
                state.isModalLoading = false;
                state.projectData.existingProcedures = action.payload.existingProcedures;
                state.projectData.projectBasicInfo = action.payload.projectBasicInfo;
                state.projectData.projectTemplates = action.payload.projectTemplates;
            })
            .addCase(GetProjectData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetProjectData.pending, state => {
                // state.isModalLoading = true;
            })
        //Get Tenant Dropdown Lists
        builder
            .addCase(GetTenantDropdownLists.fulfilled, (state, action: PayloadAction<PartnerTenantDropdownLists>) => {
                state.isModalLoading = false;
                state.tenantDropdownLists.employees = action.payload.employees;
                state.tenantDropdownLists.locations = action.payload.locations;
                state.tenantDropdownLists.projects = action.payload.projects;
                state.tenantDropdownLists.abbreviation = action.payload.abbreviation;
            })
            .addCase(GetTenantDropdownLists.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTenantDropdownLists.pending, state => {
                //state.isModalLoading = true;
            })
        //Get Tenant Location Bank Data Dropdown List
        builder
            .addCase(GetTenantLocationBankDataDropdownList.fulfilled, (state, action: PayloadAction<TenantLocationBankDataResponse>) => {
                state.isModalLoading = false;
                state.tenantDropdownLists.bankDatas = action.payload.bankDatas;
                if (state.exportFileDataTab.exportFileData) {
                    state.exportFileDataTab.exportFileData.senderCity = action.payload.city;
                }
            })
            .addCase(GetTenantLocationBankDataDropdownList.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTenantLocationBankDataDropdownList.pending, state => {
                //state.isModalLoading = true;
            })
        //Get Partner Dropdown Lists For Internal
        builder
            .addCase(GetPartnerDropdownListsForInternal.fulfilled, (state, action: PayloadAction<PartnerTenantDropdownLists>) => {
                state.isModalLoading = false;
                state.partnerDropdownLists.employees = action.payload.employees;
                state.partnerDropdownLists.locations = action.payload.locations;
            })
            .addCase(GetPartnerDropdownListsForInternal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerDropdownListsForInternal.pending, state => {
                //state.isModalLoading = true;
            })
        //Get Partner Dropdown Lists For Internal
        builder
            .addCase(GetPartnerLocationBankDataDropdownListForInternal.fulfilled, (state, action: PayloadAction<TenantLocationBankDataResponse>) => {
                state.isModalLoading = false;
                state.partnerDropdownLists.bankDatas = action.payload.bankDatas;
            })
            .addCase(GetPartnerLocationBankDataDropdownListForInternal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerLocationBankDataDropdownListForInternal.pending, state => {
                //state.isModalLoading = true;
            })
        //Get Partner Dropdown Lists For External
        builder
            .addCase(GetPartnerDropdownListsForExternal.fulfilled, (state, action: PayloadAction<PartnerDropdownListsResponse>) => {
                state.isModalLoading = false;
                state.partnerDropdownLists.locations = action.payload.locations;
                state.partnerDropdownLists.employees = action.payload.contacts;
                state.partnerDropdownLists.bankDatas = action.payload.bankDatas;
            })
            .addCase(GetPartnerDropdownListsForExternal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerDropdownListsForExternal.pending, state => {
                //state.isModalLoading = true;
            })
        //Get ProjectTemplates
        builder
            .addCase(GetPositionCreateData.fulfilled, (state, action: PayloadAction<GetPositionCreateDataResponse>) => {
                state.isModalLoading = false;
                state.dropdownLists!.articles = action.payload.articles;
            })
            .addCase(GetPositionCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPositionCreateData.pending, state => {
                //state.isModalLoading = true;
            })
        //Create Document Number And Date
        builder
            .addCase(CreateDocumentNumberAndDate.fulfilled, (state, action: PayloadAction<CreateDocumentNumberAndDateResponse>) => {
                state.isModalLoading = false;
                state.basicInfoTabData.documentDate = action.payload.documentDate;
                state.financeDocumentDatabaseState.basicInfoTabData.documentDate = action.payload.documentDate;
                state.basicInfoTabData.documentNumber = action.payload.documentNumber.toString();
                state.financeDocumentDatabaseState.basicInfoTabData.documentNumber = action.payload.documentNumber.toString();
                if (action.payload.clientNumber !== null) {
                    state.procedureInfoTabData.procedureInfo.clientNumber = action.payload.clientNumber;
                    state.financeDocumentDatabaseState.procedureInfo.clientNumber = action.payload.clientNumber;
                }
                (state.basicInfoTabData as InvoiceBasicInfo).customInvoiceNumber = action.payload.customInvoiceNumber;
                state.pdfTabData.isPdfDataReady = true;
                state.pdfTabData.documentName = action.payload.documentName;
                if (state.financeDocumentTypeId === "1") { state.relatedFinanceDocumentNumbers.requestNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "2") { state.relatedFinanceDocumentNumbers.offerNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "3") { state.relatedFinanceDocumentNumbers.orderNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "4") { state.relatedFinanceDocumentNumbers.orderConfirmationNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "5") { state.relatedFinanceDocumentNumbers.invoiceNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "8") { state.relatedFinanceDocumentNumbers.creditNoteNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "6") { state.relatedFinanceDocumentNumbers.reminderNumber = action.payload.documentNumber.toString() }
                else if (state.financeDocumentTypeId === "7") { state.relatedFinanceDocumentNumbers.dunningNumber = action.payload.documentNumber.toString() }

            })
            .addCase(CreateDocumentNumberAndDate.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application. Pdf document can not be created.';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.pdfTabData.isCreatePdfProcessing = false;
                state.pdfTabData.isPdfDataReady = false;

            })
            .addCase(CreateDocumentNumberAndDate.pending, state => {
                state.pdfTabData.isCreatePdfProcessing = true;
                state.pdfTabData.isPdfDataReady = false;
            })

        //GetIncomingDocumentNumber
        builder
            .addCase(GetIncomingDocumentNumber.fulfilled, (state, action: PayloadAction<{ documentNumber: string }>) => {
                state.isModalLoading = false;
                if (state.dropdownLists) {
                    state.dropdownLists.documentNumber = action.payload.documentNumber;
                }
            })
            .addCase(GetIncomingDocumentNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetIncomingDocumentNumber.pending, state => {
                // state.isModalLoading = true;
            })
        //Download Document File
        builder
            .addCase(DownloadDocumentFile.fulfilled, (state, action: PayloadAction<DownloadDocumentResponse>) => {
                state.isModalLoading = false;
                downloadFile(action.payload.name, action.payload.content);
            })
            .addCase(DownloadDocumentFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadDocumentFile.pending, state => {
                //state.isModalLoading = true;
            })
        //Download Document File
        builder
            .addCase(GetDocumentsAndFiles.fulfilled, (state, action: PayloadAction<DocumentsAndFilesResponse>) => {
                state.isModalLoading = false;
                // switch (action.payload.documentTypeId.toString()) {
                //     case FinanceDocumentType.Request:
                //         let request = state.documentLists.activeRequests.find(doc => doc.id === action.payload.documentId);
                //         if (request) {
                //             request.files = action.payload.files;
                //             request.documents = action.payload.documents;
                //         }
                //         break;
                //     case FinanceDocumentType.Offer:
                //         let offer = state.documentLists.activeOffers.find(doc => doc.id === action.payload.documentId);
                //         if (offer) {
                //             offer.files = action.payload.files;
                //             offer.documents = action.payload.documents;
                //         }
                //         break;
                //     case FinanceDocumentType.Order:
                //         let order = state.documentLists.activeOrders.find(doc => doc.id === action.payload.documentId);
                //         if (order) {
                //             order.files = action.payload.files;
                //             order.documents = action.payload.documents;
                //         }
                //         break;
                //     case FinanceDocumentType.OrderConfirmation:
                //         let orderConfirmation = state.documentLists.activeOrderConfirmations.find(doc => doc.id === action.payload.documentId);
                //         if (orderConfirmation) {
                //             orderConfirmation.files = action.payload.files;
                //             orderConfirmation.documents = action.payload.documents;
                //         }
                //         break;
                //     case FinanceDocumentType.Invoice:
                //         let invoice = state.documentLists.activeInvoices.find(doc => doc.id === action.payload.documentId);
                //         if (invoice) {
                //             invoice.files = action.payload.files;
                //             invoice.documents = action.payload.documents;
                //             invoice.offerDocument = action.payload.offerDocument;
                //             invoice.orderDocument = action.payload.orderDocument;
                //             invoice.orderConfirmationDocument = action.payload.orderConfirmationDocument;
                //         }
                //         break;
                //     case (FinanceDocumentType.Reminder || FinanceDocumentType.Dunning):
                //         let reminder = state.documentLists.activeReminders.find(doc => doc.id === action.payload.documentId);
                //         if (reminder) {
                //             reminder.files = action.payload.files;
                //             reminder.documents = action.payload.documents;
                //         }
                //         break;
                //     default:
                //         break;
                // }
                //add files and documents to contract item
                // let contractItem = state.documentLists.contractList.find(doc => doc.id === action.payload.documentId);
                // if (contractItem) {
                //     contractItem.files = action.payload.files;
                //     contractItem.documents = action.payload.documents;
                // }
            })
            .addCase(GetDocumentsAndFiles.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDocumentsAndFiles.pending, state => {
                //state.isModalLoading = true;
            })
        //Get All Cost Centers
        builder
            .addCase(GetAllCostCenters.fulfilled, (state, action: PayloadAction<CostCenterData[]>) => {
                state.isLoading = false;
                state.message = '';
                state.documentLists.overviewsByCostCenter = action.payload;

            })
            .addCase(GetAllCostCenters.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllCostCenters.pending, state => {
                state.isLoading = true;
            })
        //Get Cost Center Details
        builder
            .addCase(GetCostCenterDetails.fulfilled, (state, action: PayloadAction<CostCenterDetailsResponse>) => {
                const costCenter = state.documentLists.costCenterDetails.find(x => x.costCenterId === action.payload.costCenterId);
                state.isLoading = false;
                state.message = '';
                if (costCenter) {
                    costCenter.documentList = action.payload.documentList;
                } else {
                    state.documentLists.costCenterDetails.push(action.payload);
                }
            })
            .addCase(GetCostCenterDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCostCenterDetails.pending, state => {
                state.isLoading = false;
            })
        //Update File Description
        builder
            .addCase(UpdateFileDescription.fulfilled, (state, action) => {
                state.message = 'The file description is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                const index = state.attachmentsTabData.attachments.findIndex(
                    att => att.id === action.meta.arg.fileId
                );

                const attachments = [...state.attachmentsTabData.attachments];
                if (index !== -1) {
                    state.attachmentsTabData.attachments[index].description = action.meta.arg.description;
                    state.attachmentsTabData.attachments[index].isShownOnPDF = action.meta.arg.isShownOnPDF;
                    state.attachmentsTabData.attachments[index].isMainDocument = action.meta.arg.isMainDocument;

                    if (action.meta.arg.isMainDocument) {
                        attachments.forEach((attachment, idx) => {
                            if (idx !== index) {
                                attachment.isMainDocument = false;
                            }
                        });
                    }
                }
                attachments.sort((a, b) => Number(b.isMainDocument) - Number(a.isMainDocument));
                state.attachmentsTabData.attachments = attachments;
            })
            .addCase(UpdateFileDescription.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateFileDescription.pending, state => {
                state.isModalLoading = true;
            })

        //Reorder Attachments
        builder
            .addCase(ReorderAttachments.fulfilled, (state, action) => {
                state.message = 'The attachments have been successfully reordered.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(ReorderAttachments.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReorderAttachments.pending, state => {
                state.isModalLoading = true;
            })

        //Update Procedure
        builder
            .addCase(UpdateProcedure.fulfilled, (state, action: PayloadAction<UpdateProcedureInfoResponse>) => {
                state.message = 'The procedure is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.basicInfoTabData.statusId = action.payload.documentStatusId;
                state.financeDocumentDatabaseState.procedureInfo = state.procedureInfoTabData.procedureInfo;
                state.financeDocumentDatabaseState.basicInfoTabData.statusId = action.payload.documentStatusId;
                // state.procedureInfoTabData = action.payload.financeProcedure;
            })
            .addCase(UpdateProcedure.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProcedure.pending, state => {
                state.isModalLoading = true;
            })
        //Upload Procedure File
        builder
            .addCase(UploadProcedureFile.fulfilled, (state, action: PayloadAction<ProcedureFilesResponse>) => {
                state.message = action.payload.message ?? 'The procedure file is successfully uploaded.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.procedureInfoTabData.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
                state.financeDocumentDatabaseState.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
            })
            .addCase(UploadProcedureFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UploadProcedureFile.pending, state => {
                // state.isModalLoading = true;
            })
        //Delete Procedure File
        builder
            .addCase(DeleteProcedureFile.fulfilled, (state, action: PayloadAction<ProcedureFilesResponse>) => {
                state.message = action.payload.message ?? 'The procedure file is successfully deleted.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.procedureInfoTabData.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
                state.financeDocumentDatabaseState.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
            })
            .addCase(DeleteProcedureFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteProcedureFile.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Procedure File
        builder
            .addCase(UpdateProcedureFile.fulfilled, (state, action: PayloadAction<ProcedureFilesResponse>) => {
                state.message = action.payload.message ?? 'The procedure file is successfully updated.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
                state.procedureInfoTabData.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
                state.financeDocumentDatabaseState.procedureInfo.files = action.payload.files.sort((a, b) => Number(b.isMain) - Number(a.isMain));
            })
            .addCase(UpdateProcedureFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProcedureFile.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Procedure File
        builder
            .addCase(DownloadProcedureFile.fulfilled, (state, action: PayloadAction<ProcedureInfoFile>) => {
                // state.statusCode = "200";
                // state.isModalLoading = false;
                // state.showNotification = true;
                downloadFile(action.payload.fileName, action.payload.fileBase64);
                // state.procedureInfoTabData.procedureFileForDownload = action.payload;
            })
            .addCase(DownloadProcedureFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DownloadProcedureFile.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Procedure File
        builder
            .addCase(GetProcedure.fulfilled, (state, action: PayloadAction<ProcedureInfo>) => {
                state.statusCode = "";
                state.isModalLoading = false;
                state.showNotification = false;
                state.procedureInfoTabData.procedureInfo = { ...action.payload, docType: state.procedureInfoTabData.procedureInfo.docType, noCostCenter: state.procedureInfoTabData.procedureInfo.noCostCenter };
                state.financeDocumentDatabaseState.procedureInfo = { ...action.payload, docType: state.procedureInfoTabData.procedureInfo.docType, noCostCenter: state.procedureInfoTabData.procedureInfo.noCostCenter };
            })
            .addCase(GetProcedure.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProcedure.pending, state => {
                // state.isModalLoading = true;
            })
        //Update Procedure File
        builder
            .addCase(UpdateDocumentPdfSettings.fulfilled, (state, action: PayloadAction<PdfSettingResponse>) => {
                state.statusCode = "200";
                state.message = action.payload.message;
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(UpdateDocumentPdfSettings.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateDocumentPdfSettings.pending, state => {
            })
        //#endregion
        //#region Refrence data

        //CreateSource
        builder
            .addCase(CreateFinanceSource.fulfilled, (state, action: PayloadAction<TranslationList[]>) => {
                state.message = 'The source is successfully created on application default language.';
                state.statusCode = "200";
                state.showNotification = true;
                state.sourceInfo = {
                    isSourceDropdownLoading: false,
                    newSourceId: ""
                }
                if (state.dropdownLists) {
                    state.dropdownLists.sources = action.payload;
                }

            })
            .addCase(CreateFinanceSource.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.sourceInfo.isSourceDropdownLoading = false;
            })
            .addCase(CreateFinanceSource.pending, state => {
                state.sourceInfo.isSourceDropdownLoading = true;
            })

        //CreateCategory
        builder
            .addCase(CreateCategory.fulfilled, (state, action: PayloadAction<TranslationList[]>) => {
                state.message = 'The category is successfully created on application default language.';
                state.statusCode = "200";
                state.showNotification = true;
                state.categoryInfo = {
                    isCategoryDropdownLoading: false,
                    newCategoryId: null
                };

                if (state.dropdownLists) {
                    state.dropdownLists.categories = action.payload;
                }

            })
            .addCase(CreateCategory.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.categoryInfo.isCategoryDropdownLoading = false;
            })
            .addCase(CreateCategory.pending, state => {
                state.categoryInfo.isCategoryDropdownLoading = true;
            })

        //CreatePaymentMethod
        builder
            .addCase(CreatePaymentMethod.fulfilled, (state, action: PayloadAction<TranslationList[]>) => {
                state.message = 'The payment method is successfully created on application default language.';
                state.statusCode = "200";
                state.showNotification = true;
                state.paymentMethodInfo = {
                    isPaymentMethodDropdownLoading: false,
                    newPaymentMethodId: null
                };
                if (state.dropdownLists) {
                    state.dropdownLists.paymentMethods = action.payload;
                }

            })
            .addCase(CreatePaymentMethod.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
                state.paymentMethodInfo.isPaymentMethodDropdownLoading = false;
            })
            .addCase(CreatePaymentMethod.pending, state => {
                state.paymentMethodInfo.isPaymentMethodDropdownLoading = true;
            })
        //#endregion
    }
});

export const { removeDocumentFromList, restoreDocumentToList, resetDocument, resetFinanceDocumentSlice, resetDocumentId, resetStatusCode, setFinanceDocumentModelValidity,
    setBasicInfoTabProp, setFinanceDocumentType, setPrevFinanceDocument, resetPrevFinanceDocument, resetProjectData, resetDocumentVersionsHistory,
    resetPosition, setPositions, addPositionInList, setPosition, addArticleInPosition, removePositionFromList, updatePositionInList, resetUpdatePositionsInDb,
    setNewPositionIds, setNewReminderPosition, setDunningPositions, setNewReminderPositionIds, resetReminderPosition, /* resetPaymentsInReminderPosition, */ setReminderPositions, addReminderPositionInList, setReminderPosition,
    generateNewIdsForDunningPositions, removeReminderPositionFromList, updateReminderPositionInList, resetUpdateReminderPositionsInDb,
    setSelectedTextblocksInTab, setTextblockFromTemplate, resetPdfData, generateNewIdsForDocumentTextBlocks,
    /* addAttachment, updateAttachment, downloadLocalAttachment */ deleteAttachmentInSlice, setAttachmentFiles, resetSource, setIsUploadSpaceOpen, resetTenantLocationsBankData,
    setAdditionalCurrency, setExchangeRate, setPdfLanguage, setAdditionalPdfLanguage, setShowArticle, setShowWage, setShowMaterial, setPdfProcessingState, /* resetDocumentForDownload, */
    setProcedureInfoTabProp, resetProcedureInfoTabProps, resetProcedureInfoTabParticipantsAndContractData, /* addProcedureFile, deleteProcedureFile, deleteProcedureFiles, updateProcedureFile, downloadProcedureFile */ } = FinanceDocumentSlice.actions;

export default FinanceDocumentSlice.reducer;